import React, { useState, useEffect, useCallback } from 'react';
import { brokerFundingDate } from '@/utils/broker-recon/fundingDate';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Printer, X, Upload, FileText, CheckCircle2, Loader2, CloudUpload, Trash2, Check, Pencil, RotateCw, ListChecks, FilePlus2 } from 'lucide-react';
import { downloadBrokerReconPDF } from '@/utils/broker-recon/pdfGenerator';
import { PDFDocument, degrees } from 'pdf-lib';
import { UserService } from '@/services/userService';
import { compSet, resolveBrokerCommissionSplit } from '@/services/compensationService';
import { useAuth } from '@/contexts/useAuth';
import type { User } from '@/types/user';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { supabase } from '@/utils/supabase';
import { reserveLoanStorageObject } from '@/services/loanStorageAuthorizationService';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { LoanAutocomplete, type EncompassLoan } from './LoanAutocomplete';
import { FinalizedPayoutsTable } from './FinalizedPayoutsTable';

// Below this confidence, extracted settlement fees are NOT auto-applied to the
// form — a garbled scan once auto-filled a merged "Mortgage Broker Fee …
// Funds retained by lender" line as a $16,000 fee at 0.65 confidence.
const FEE_AUTO_APPLY_MIN_CONFIDENCE = 0.8;

interface LenderFee {
  id: string;
  name: string;
  totalFee: number;
  borrowerPAC: number;
  borrowerPOC: number;
  lenderPOC: number;
  reimbursement: number;
  reimburseTo: 'NA' | 'Loan Officer' | 'Mentor';
}

interface MentorData {
  mentorId: string;
  mentorName: string;
  mentorSplitPercent: number;
  brokeredDealsCompleted: number;
}

interface Investor {
  id: string;
  investor_name: string;
}

type BrokerSplitBasis =
  | {
      source: 'history';
      historyId: string;
      effectiveDate: string;
    }
  | {
      source: 'snapshot';
      historyId: string | null;
      effectiveDate: string | null;
    }
  | {
      source: 'manual';
      historyId: null;
      effectiveDate: null;
    };

interface UploadedDocument {
  id: string;
  file: File | null; // Can be null for restored documents
  fileName?: string; // Display name - use when file is null
  documentType: 'settlement_statement' | 'final_check' | 'combined' | 'unknown';
  confidence: number;
  storagePath: string;
  extractedFields?: any;
  uploadedAt: Date;
  needsClassification?: boolean;
  classificationReasoning?: string;
  queueId?: string; // GPU worker queue ID for tracking extraction
  // Encompass push tracking
  pushedToEncompass?: boolean;
  encompassAttachmentId?: string;
  pushedAt?: Date;
  pushingToEncompass?: boolean; // Loading state
  // Combined document tracking
  isCombined?: boolean;
  splitFrom?: string; // ID of the combined document this was split from
  // Custom display names for split files
  splitFileNames?: {
    original?: string;
    settlementStatement?: string;
    brokerCheck?: string;
  };
  // Extraction progress tracking
  progress?: number;  // 0-100 extraction progress
  processingStatus?: 'queued' | 'extracting' | 'classifying' | 'complete' | 'failed';
  // Split failure state. The server refuses to produce a split document it
  // cannot bind to the loan, so a combined document can extract cleanly and
  // still have no usable split files. Persist that per document instead of
  // relying on a toast, and keep the extracted fields.
  splitError?: string;
  retryingSplit?: boolean;
}

/**
 * A combined document is only usable when BOTH split files exist: pushing it
 * uploads the settlement statement and the broker check as two separate
 * documents. The server reports a `splitError` when it refuses to create or
 * bind a file, but a page group it never detected produces no error at all, so
 * "no error" does not mean "complete". Derive the failure state from the paths
 * themselves and fall back to a plain description.
 */
export function describeSplitFailure(
  splitResult: { settlementStatementPath?: string | null; brokerCheckPath?: string | null } | null | undefined,
  serverSplitError?: string | null
): string | undefined {
  const hasBoth = Boolean(
    splitResult?.settlementStatementPath && splitResult?.brokerCheckPath
  );
  if (hasBoth) {
    return undefined;
  }
  if (serverSplitError) {
    return serverSplitError;
  }
  if (!splitResult?.settlementStatementPath && !splitResult?.brokerCheckPath) {
    return 'No split documents were created from this combined file.';
  }
  return splitResult?.settlementStatementPath
    ? 'Only the settlement statement was created; the broker check is missing.'
    : 'Only the broker check was created; the settlement statement is missing.';
}

export const BrokerRecon: React.FC = () => {
  const { user } = useAuth();
  const { companyProfile } = useCompanyProfile();

  // Loan officers from database
  const [loanOfficers, setLoanOfficers] = useState<User[]>([]);
  const [loadingOfficers, setLoadingOfficers] = useState(true);

  // Investors from database
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loadingInvestors, setLoadingInvestors] = useState(true);

  // Pending reconciliations from payouts table
  const [pendingRecons, setPendingRecons] = useState<any[]>([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [currentPayoutId, setCurrentPayoutId] = useState<string | null>(null);

  // Selected loan from autocomplete
  const [selectedLoan, setSelectedLoan] = useState<EncompassLoan | null>(null);

  // Loan details state - starts empty, populated by loan selection or PDF extraction
  const [borrowerName, setBorrowerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loanNumber, setLoanNumber] = useState('');
  const [lender, setLender] = useState('');
  const [processor, setProcessor] = useState('');
  const [loanOfficerId, setLoanOfficerId] = useState('');
  const [brokerSplit, setBrokerSplit] = useState(85);
  const [fundedDate, setFundedDate] = useState('');
  const [brokerSplitBasis, setBrokerSplitBasis] = useState<BrokerSplitBasis | null>(null);
  const [brokerSplitResolving, setBrokerSplitResolving] = useState(false);
  const [brokerSplitResolutionError, setBrokerSplitResolutionError] = useState<string | null>(null);
  const brokerSplitResolutionRequestRef = React.useRef(0);
  const [brokerSplitReassigned, setBrokerSplitReassigned] = useState(false);

  // Mentor state
  const [mentorData, setMentorData] = useState<MentorData | null>(null);
  const mentorAbortRef = React.useRef<AbortController | null>(null);

  // Financial calculations state - starts empty, populated by PDF extraction or manual entry
  const [loanAmount, setLoanAmount] = useState(0);

  // Lender fees state - starts empty, populated by PDF extraction or manual entry
  const [lenderFees, setLenderFees] = useState<LenderFee[]>([]);

  const [newFeeName, setNewFeeName] = useState('');
  const [newFeeData, setNewFeeData] = useState({
    totalFee: '',
    borrowerPAC: '',
    borrowerPOC: '',
    lenderPOC: '',
    reimbursement: '',
    reimburseTo: 'NA' as 'NA' | 'Loan Officer' | 'Mentor'
  });
  const [resendPDF, setResendPDF] = useState(false);

  // NEW: Single document upload state
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [renamingDocId, setRenamingDocId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // Split file rename state: "docId:fileType" format (e.g., "abc123:settlement" or "abc123:check" or "abc123:original")
  const [renamingSplitFile, setRenamingSplitFile] = useState<string | null>(null);
  const [splitRenameValue, setSplitRenameValue] = useState('');

  // Gross Check Reconciliation
  const [grossCheckReceived, setGrossCheckReceived] = useState(0);
  const [grossCheckInput, setGrossCheckInput] = useState(''); // Raw input for typing
  const [currentPayoutStatus, setCurrentPayoutStatus] = useState<'pending' | 'approved' | 'paid'>('pending');
  const [pushingToAccounting, setPushingToAccounting] = useState(false);

  // View mode: 'new' = form view, 'finalized' = finalized records table
  const [viewMode, setViewMode] = useState<'new' | 'finalized'>('new');
  const [approvedRecons, setApprovedRecons] = useState<any[]>([]);
  const [approvedReconsLoading, setApprovedReconsLoading] = useState(false);

  // Derived booleans from currentPayoutStatus
  const isFinalized = currentPayoutStatus === 'approved';
  const isPaid = currentPayoutStatus === 'paid';
  const isReadOnly = currentPayoutStatus !== 'pending';

  // Cancel any active rename when entering read-only mode
  useEffect(() => {
    if (isReadOnly) {
      setRenamingDocId(null);
      setRenamingSplitFile(null);
    }
  }, [isReadOnly]);

  // Standard Fees (hardcoded for now, future: pull from org settings)
  const PROCESSING_FEE = 750;
  const EO_INSURANCE = 50;

  // Set company name from profile when loaded
  useEffect(() => {
    if (companyProfile?.company_name && !companyName) {
      setCompanyName(companyProfile.company_name);
    }
  }, [companyProfile]);

  // Sync grossCheckInput when grossCheckReceived changes externally (e.g., AI extraction)
  useEffect(() => {
    if (grossCheckReceived > 0) {
      setGrossCheckInput(formatCurrency(grossCheckReceived));
    } else {
      setGrossCheckInput('');
    }
  }, [grossCheckReceived]);

  // Load loan officers on mount
  useEffect(() => {
    const loadLoanOfficers = async () => {
      if (!user?.organization_id) {
        setLoadingOfficers(false);
        return;
      }

      const { data, error } = await UserService.getLoanOfficers(user.organization_id);

      if (error) {
        console.error('Error loading loan officers:', error);
        setLoadingOfficers(false);
        return;
      }

      if (data) {
        setLoanOfficers(data);
        // Set first loan officer as default if available
        if (data.length > 0) {
          setLoanOfficerId(data[0].id);
        }
      }

      setLoadingOfficers(false);
    };

    loadLoanOfficers();
  }, [user?.organization_id]);

  // A new payout derives its split from the loan's funded date. Existing
  // pending/approved/paid payouts keep the split snapshot stored on the row.
  useEffect(() => {
    const requestId = ++brokerSplitResolutionRequestRef.current;

    if ((currentPayoutId && !brokerSplitReassigned) || !loanOfficerId || !fundedDate) {
      setBrokerSplitResolving(false);
      if (!currentPayoutId) {
        setBrokerSplitBasis(null);
        setBrokerSplitResolutionError(null);
      }
      return;
    }

    setBrokerSplitResolving(true);
    setBrokerSplitBasis(null);
    setBrokerSplitResolutionError(null);

    void resolveBrokerCommissionSplit(loanOfficerId, fundedDate)
      .then((resolution) => {
        if (brokerSplitResolutionRequestRef.current !== requestId) {
          return;
        }
        setBrokerSplit(resolution.brokerCommissionSplit);
        setBrokerSplitBasis({
          source: 'history',
          historyId: resolution.historyId,
          effectiveDate: resolution.effectiveDate
        });
      })
      .catch((error: unknown) => {
        if (brokerSplitResolutionRequestRef.current !== requestId) {
          return;
        }
        const message = error instanceof Error
          ? error.message
          : 'Failed to resolve the funding-date broker split';
        setBrokerSplitResolutionError(message);
      })
      .finally(() => {
        if (brokerSplitResolutionRequestRef.current === requestId) {
          setBrokerSplitResolving(false);
        }
      });
  }, [currentPayoutId, fundedDate, loanOfficerId, brokerSplitReassigned, selectedLoan?.id]);

  // Load wholesale investors on mount
  useEffect(() => {
    const loadInvestors = async () => {
      const { data, error } = await supabase
        .from('investors')
        .select('id, investor_name')
        .eq('can_do_wholesale', true)
        .eq('wholesale_status', 'Approved')
        .order('investor_name');

      if (error) {
        console.error('Error loading investors:', error);
        setLoadingInvestors(false);
        return;
      }

      if (data) {
        setInvestors(data);
        // Set first investor as default if available
        if (data.length > 0) {
          setLender(data[0].id);
        }
      }

      setLoadingInvestors(false);
    };

    loadInvestors();
  }, []);

  // Load pending broker recon payouts on mount
  useEffect(() => {
    const loadPendingRecons = async () => {
      if (!user?.organization_id) {
        setLoadingPending(false);
        return;
      }

      const { data, error } = await supabase
        .from('payouts')
        .select('*, recipient:recipient_id(first_name, last_name), lender:lender_id(investor_name)')
        .eq('organization_id', user.organization_id)
        .eq('source_type', 'broker-recon')
        .neq('payout_type', 'mentor') // Hide mentor payouts from sidebar list
        .eq('status', 'pending')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error loading pending recons:', error);
      } else if (data) {
        setPendingRecons(data);
      }

      setLoadingPending(false);
    };

    loadPendingRecons();
  }, [user?.organization_id]);

  // Reload pending recons (call after save to refresh list)
  const reloadPendingRecons = async () => {
    if (!user?.organization_id) {
return;
}

    const { data, error } = await supabase
      .from('payouts')
      .select('*, recipient:recipient_id(first_name, last_name), lender:lender_id(investor_name)')
      .eq('organization_id', user.organization_id)
      .eq('source_type', 'broker-recon')
      .neq('payout_type', 'mentor') // Hide mentor payouts from sidebar list
      .eq('status', 'pending')
      .order('updated_at', { ascending: false });

    if (!error && data) {
      setPendingRecons(data);
    }
  };

  // Resolve mentor data for a loan officer
  const resolveMentorData = useCallback(async (officer: User) => {
    // Cancel any in-flight mentor resolution
    if (mentorAbortRef.current) {
      mentorAbortRef.current.abort();
    }

    // Guard: no mentorship or self-mentorship
    if (
      !officer.mentorshipActive ||
      !officer.mentorId ||
      !officer.brokerMentorCommissionSplit ||
      officer.brokerMentorCommissionSplit <= 0 ||
      officer.mentorId === officer.id
    ) {
      setMentorData(null);
      return;
    }

    const controller = new AbortController();
    mentorAbortRef.current = controller;

    const { data: mentor } = await supabase
      .from('users')
      .select('full_name, first_name, last_name')
      .eq('id', officer.mentorId)
      .eq('organization_id', user?.organization_id)
      .single();

    // Bail if aborted (LO changed during fetch)
    if (controller.signal.aborted) {
return;
}

    if (mentor) {
      setMentorData({
        mentorId: officer.mentorId,
        mentorName: mentor.full_name || `${mentor.first_name} ${mentor.last_name}`.trim(),
        mentorSplitPercent: officer.brokerMentorCommissionSplit,
        brokeredDealsCompleted: officer.brokeredDealsCompleted ?? 0
      });
    } else {
      setMentorData(null);
    }
  }, []);

  // Handle loading a pending reconciliation
  const handleLoadPendingRecon = async (payout: any) => {
    setBrokerSplitReassigned(false);
    // Track which payout is loaded
    brokerSplitResolutionRequestRef.current += 1;
    setCurrentPayoutId(payout.id);

    // Populate all form fields from the payout record
    setBorrowerName(payout.borrower_name || '');
    setLoanNumber(payout.loan_number || '');
    setLender(payout.lender_id || '');
    setProcessor(payout.processor || '');
    setLoanOfficerId(payout.recipient_id || '');
    const storedBrokerSplit = Number(payout.broker_split_percent);
    if (
      payout.broker_split_percent === null ||
      payout.broker_split_percent === undefined ||
      !Number.isFinite(storedBrokerSplit)
    ) {
      setBrokerSplit(0);
      setBrokerSplitBasis(null);
      setBrokerSplitResolutionError(
        'This saved payout has no broker split snapshot. Enter and verify the split before saving.'
      );
    } else {
      setBrokerSplit(storedBrokerSplit);
      setBrokerSplitBasis({
        source: 'snapshot',
        historyId: payout.broker_split_history_id || null,
        effectiveDate: payout.broker_split_effective_date || null
      });
      setBrokerSplitResolutionError(null);
    }
    setBrokerSplitResolving(false);
    setFundedDate(payout.fund_date || '');
    setLoanAmount(payout.loan_amount || 0);

    // Check reconciliation
    setGrossCheckReceived(payout.gross_check_received || 0);

    // Load lender fees if saved
    if (payout.lender_fees) {
      const fees = typeof payout.lender_fees === 'string'
        ? JSON.parse(payout.lender_fees)
        : payout.lender_fees;
      if (Array.isArray(fees) && fees.length > 0) {
        setLenderFees(fees);
      }
    }

    // Restore document paths from saved payout
    const restoredDocs: UploadedDocument[] = [];

    // Restore combined document (original upload) if exists
    if (payout.combined_document_path) {
      const fileName = payout.combined_document_path.split('/').pop() || 'Combined Document.pdf';
      restoredDocs.push({
        id: `combined-${payout.id}`,
        file: null,
        fileName,
        documentType: 'combined',
        confidence: 1.0,
        storagePath: payout.combined_document_path,
        uploadedAt: new Date(payout.updated_at || payout.created_at),
        needsClassification: false,
        isCombined: true,
        // Include split result paths so Encompass push works
        extractedFields: {
          splitResult: {
            settlementStatementPath: payout.settlement_statement_path,
            brokerCheckPath: payout.final_check_path
          }
        },
        // A saved recon records only the paths, so an incomplete split comes
        // back as nulls with no explanation. Rebuild the failure state from
        // what was stored, otherwise the row reads as "split pending" forever
        // and offers no way to recover.
        splitError: describeSplitFailure({
          settlementStatementPath: payout.settlement_statement_path,
          brokerCheckPath: payout.final_check_path
        })
      });
    }

    // Restore split documents if no combined (individual uploads)
    if (!payout.combined_document_path) {
      if (payout.settlement_statement_path) {
        const fileName = payout.settlement_statement_path.split('/').pop() || 'Settlement Statement.pdf';
        restoredDocs.push({
          id: `settlement-${payout.id}`,
          file: null,
          fileName,
          documentType: 'settlement_statement',
          confidence: 1.0,
          storagePath: payout.settlement_statement_path,
          uploadedAt: new Date(payout.updated_at || payout.created_at),
          needsClassification: false
        });
      }
      if (payout.final_check_path) {
        const fileName = payout.final_check_path.split('/').pop() || 'Broker Check.pdf';
        restoredDocs.push({
          id: `check-${payout.id}`,
          file: null,
          fileName,
          documentType: 'final_check',
          confidence: 1.0,
          storagePath: payout.final_check_path,
          uploadedAt: new Date(payout.updated_at || payout.created_at),
          needsClassification: false
        });
      }
    }

    if (restoredDocs.length > 0) {
      setUploadedDocuments(restoredDocs);
    }

    // Set payout status (drives read-only mode for approved/paid)
    setCurrentPayoutStatus(payout.status || 'pending');

    // Load companion mentor payout if exists
    if (user?.organization_id && payout.loan_number) {
      const { data: mentorPayout } = await supabase
        .from('payouts')
        .select('*, recipient:recipient_id(full_name, first_name, last_name)')
        .eq('loan_number', payout.loan_number)
        .eq('organization_id', user.organization_id)
        .eq('payout_type', 'mentor')
        .eq('source_type', 'broker-recon')
        .maybeSingle();

      if (mentorPayout) {
        const recipientData = mentorPayout.recipient as any;
        setMentorData({
          mentorId: mentorPayout.recipient_id,
          mentorName: recipientData?.full_name || `${recipientData?.first_name || ''} ${recipientData?.last_name || ''}`.trim(),
          mentorSplitPercent: mentorPayout.broker_split_percent || 50,
          brokeredDealsCompleted: 0 // Will be refreshed from LO's user record below
        });

        // Refresh brokered_deals_completed from LO's current user record
        const { data: loUser } = await supabase
          .from('users')
          .select('brokered_deals_completed, mentorship_active')
          .eq('id', payout.recipient_id)
          .single();

        if (loUser) {
          setMentorData(prev => prev ? {
            ...prev,
            brokeredDealsCompleted: loUser.brokered_deals_completed ?? 0
          } : null);
        }
      } else {
        // No saved mentor payout — resolve from LO's current config
        const selectedOfficer = loanOfficers.find(o => o.id === payout.recipient_id);
        if (selectedOfficer) {
          await resolveMentorData(selectedOfficer);
        } else {
          setMentorData(null);
        }
      }
    }

    toast.success('Loaded reconciliation', {
      description: `${payout.loan_number} - ${payout.borrower_name || 'Unknown borrower'}`
    });
  };

  // Calculations

  // Helper to safely parse float values - returns 0 if NaN
  const safeParseFloat = (value: any): number => {
    if (value === undefined || value === null || value === '') {
return 0;
}
    const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  };

  // Helper function for money calculations - round to cents, with NaN protection
  const roundToCents = (amount: number) => {
    if (isNaN(amount)) {
return 0;
}
    return Math.round(amount * 100) / 100;
  };

  // Fee totals (round to avoid floating point precision issues) - with NaN guards
  const totalFees = roundToCents(lenderFees.reduce((sum, fee) => sum + (isNaN(fee.totalFee) ? 0 : fee.totalFee), 0));
  const totalBorrowerPAC = roundToCents(lenderFees.reduce((sum, fee) => sum + (isNaN(fee.borrowerPAC) ? 0 : fee.borrowerPAC), 0));
  const totalBorrowerPOC = roundToCents(lenderFees.reduce((sum, fee) => sum + (isNaN(fee.borrowerPOC) ? 0 : fee.borrowerPOC), 0));
  const totalLenderPOC = roundToCents(lenderFees.reduce((sum, fee) => sum + (isNaN(fee.lenderPOC) ? 0 : fee.lenderPOC), 0));
  const totalReimbursement = roundToCents(lenderFees.reduce((sum, fee) => sum + (isNaN(fee.reimbursement) ? 0 : fee.reimbursement), 0));

  // Check Reconciliation Calculations - with NaN guards
  // Simple: Gross Check Received should equal Total Fees from the fee table
  const safeGrossCheck = isNaN(grossCheckReceived) ? 0 : grossCheckReceived;
  const checkVariance = roundToCents(safeGrossCheck - totalFees);
  const isReconciled = Math.abs(checkVariance) < 0.01;

  // LO Reimbursements (only fees where reimburse_to = 'Loan Officer')
  const totalLOReimbursements = roundToCents(
    lenderFees
      .filter(fee => fee.reimburseTo === 'Loan Officer')
      .reduce((sum, fee) => sum + (isNaN(fee.reimbursement) ? 0 : fee.reimbursement), 0)
  );

  // Mentor Reimbursements (only fees where reimburse_to = 'Mentor')
  const totalMentorReimbursements = roundToCents(
    lenderFees
      .filter(fee => fee.reimburseTo === 'Mentor')
      .reduce((sum, fee) => sum + (isNaN(fee.reimbursement) ? 0 : fee.reimbursement), 0)
  );

  // All reimbursements that reduce the net-for-split (LO + Mentor)
  const totalAllReimbursements = roundToCents(totalLOReimbursements + totalMentorReimbursements);

  // Commission Calculation (Brokered Loan - Pre-Split Deductions) - with NaN guards
  const safeBrokerSplit = isNaN(brokerSplit) ? 85 : brokerSplit;
  const baseCheck = safeGrossCheck; // Base check = Gross Check Received (must match Total Fees)
  // E&O is a flat $50 deducted from the LO/Mentor AFTER the split (loEAndO/mentorEAndO below) and
  // collected by the company via its add-back. It must NOT come off the top here, or the LO side is
  // double-charged and the recon ends $50 short.
  const netForSplit = roundToCents(baseCheck - PROCESSING_FEE - totalAllReimbursements);
  const loCommissionBase = roundToCents(netForSplit * (safeBrokerSplit / 100));
  const loTotalCommission = roundToCents(loCommissionBase + totalAllReimbursements);
  // Residual (not an independent multiply) so loCommissionBase + companyCommissionBase === netForSplit exactly.
  const companyCommissionBase = roundToCents(netForSplit - loCommissionBase);
  const companyTotalRevenue = roundToCents(companyCommissionBase + PROCESSING_FEE + EO_INSURANCE);

  // Mentor Commission Split (from LO's commission base, before reimbursements)
  const mentorSplitPercent = mentorData?.mentorSplitPercent ?? 0;
  const mentorCommission = mentorData ? roundToCents(loCommissionBase * (mentorSplitPercent / 100)) : 0;
  const loCommissionAfterMentor = roundToCents(loCommissionBase - mentorCommission);
  const loTotalAfterMentor = roundToCents(loCommissionAfterMentor + totalLOReimbursements);
  const mentorTotal = roundToCents(mentorCommission + totalMentorReimbursements);

  // E&O split: proportional between LO and mentor
  const loEAndO = mentorData ? roundToCents(EO_INSURANCE * ((100 - mentorSplitPercent) / 100)) : EO_INSURANCE;
  const mentorEAndO = mentorData ? EO_INSURANCE - loEAndO : 0;

  // Validation for Push to Accounting. A new payout must have a funding-date
  // ledger match (or an explicit manual override); an existing payout already
  // carries its immutable split snapshot.
  const hasBrokerSplitBasis = brokerSplitBasis !== null;
  const brokerSplitHistoryId = brokerSplitBasis?.source === 'manual'
    ? null
    : brokerSplitBasis?.historyId ?? null;
  const brokerSplitEffectiveDate = brokerSplitBasis?.source === 'manual'
    ? null
    : brokerSplitBasis?.effectiveDate ?? null;
  const canPushToAccounting = isReconciled &&
    currentPayoutStatus === 'pending' &&
    Boolean(fundedDate) &&
    hasBrokerSplitBasis &&
    !brokerSplitResolving &&
    !brokerSplitResolutionError;

  const pushToAccountingTitle = isReadOnly
    ? 'Already pushed to accounting'
    : !isReconciled
      ? 'Balance must be $0 to push to accounting'
      : !fundedDate
        ? 'Funded date is required to resolve the broker split'
        : brokerSplitResolving
          ? 'Broker split is loading'
          : brokerSplitResolutionError || 'Push LO payout to accounting';

  const validateFundingDatedBrokerSplit = (): boolean => {
    if (!fundedDate) {
      toast.error('Funded date is required', {
        description: 'The funded date determines which broker commission split applies.'
      });
      return false;
    }
    if (brokerSplitResolving) {
      toast.info('Broker split is still loading', {
        description: 'Wait for the funding-date compensation lookup to finish.'
      });
      return false;
    }
    if (brokerSplitResolutionError) {
      toast.error('Broker split could not be resolved', {
        description: brokerSplitResolutionError
      });
      return false;
    }
    if (!hasBrokerSplitBasis) {
      toast.error('Broker split is not ready', {
        description: 'Select a loan officer and funded date, then verify the effective split.'
      });
      return false;
    }
    return true;
  };

  const formatCurrency = (amount: number) => {
    // Protect against NaN display
    const safeAmount = isNaN(amount) ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(safeAmount);
  };

  const handleDeleteFee = (id: string) => {
    setLenderFees(lenderFees.filter(fee => fee.id !== id));
  };

  const handleNewFeeChange = (field: keyof typeof newFeeData, value: string) => {
    // Allow empty, numbers, and decimals only (max 2 decimal places)
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setNewFeeData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddFee = () => {
    if (!newFeeName.trim()) {
      toast.error('Please enter a fee name');
      return;
    }

    // Parse all string values to numbers, defaulting to 0
    const lenderPOC = parseFloat(newFeeData.lenderPOC) || 0;
    const totalFee = parseFloat(newFeeData.totalFee) || 0;
    const reimburseTo = newFeeData.reimburseTo;

    // Auto-populate reimbursement if "Loan Officer" or "Mentor" selected - use totalFee (not lenderPOC)
    const reimbursement = (reimburseTo === 'Loan Officer' || reimburseTo === 'Mentor')
      ? totalFee
      : (parseFloat(newFeeData.reimbursement) || 0);

    const newFee: LenderFee = {
      id: Date.now().toString(),
      name: newFeeName.trim(),
      totalFee: parseFloat(newFeeData.totalFee) || 0,
      borrowerPAC: parseFloat(newFeeData.borrowerPAC) || 0,
      borrowerPOC: parseFloat(newFeeData.borrowerPOC) || 0,
      lenderPOC,
      reimbursement,
      reimburseTo
    };

    setLenderFees([...lenderFees, newFee]);

    // Clear the form
    setNewFeeName('');
    setNewFeeData({
      totalFee: '',
      borrowerPAC: '',
      borrowerPOC: '',
      lenderPOC: '',
      reimbursement: '',
      reimburseTo: 'NA'
    });

    toast.success(`Added fee: ${newFeeName.trim()}`);
  };

  // Format date for display (MM/DD/YYYY)
  // Note: Parse YYYY-MM-DD without timezone conversion to avoid off-by-one errors
  const formatDateForDisplay = (dateString: string) => {
    if (!dateString) {
return '';
}
    // Check if it's already in a format we can parse as local
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // YYYY-MM-DD format - parse as local date to avoid timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    }
    // Fallback for other formats
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Handle loan selection from autocomplete (Encompass loan)
  // Auto-detects and loads existing pending draft if one exists for this loan
  const handleLoanSelect = useCallback((loan: EncompassLoan) => {
    // Check if there's an existing pending draft for this loan
    const existingPending = pendingRecons.find(p =>
      p.status === 'pending' && (
        (p.encompass_loan_id && p.encompass_loan_id === loan.id) ||
        (p.loan_number && loan.trans_details_loan_number && p.loan_number === loan.trans_details_loan_number)
      )
    );

    if (existingPending) {
      // Auto-load the existing draft
      setSelectedLoan(loan);
      handleLoadPendingRecon(existingPending);
      toast.info('Resumed pending draft', {
        description: `Loaded existing reconciliation for loan ${loan.trans_details_loan_number || ''}`
      });
      return;
    }

    // No existing draft - start fresh
    // Clear all existing form state (prevents stale data from previous loan)
    setBorrowerName('');
    setLoanNumber('');
    setLoanAmount(0);
    setLoanOfficerId('');
    setProcessor('');
    setFundedDate('');
    setLender('');
    setLenderFees([]);
    setGrossCheckReceived(0);
    setGrossCheckInput('');
    setCurrentPayoutStatus('pending');
    setUploadedDocuments([]);
    setCurrentPayoutId(null);
    setMentorData(null);
    brokerSplitResolutionRequestRef.current += 1;
    setBrokerSplitBasis(null);
    setBrokerSplitResolving(false);
    setBrokerSplitResolutionError(null);

    // Now set the new selected loan
    setSelectedLoan(loan);

    // Auto-fill borrower name from new loan
    const borrowerFullName = `${loan.borrower_first_name || ''} ${loan.borrower_last_name || ''}`.trim();
    if (borrowerFullName) {
      setBorrowerName(borrowerFullName);
    }

    // Auto-fill loan number
    if (loan.trans_details_loan_number) {
      setLoanNumber(loan.trans_details_loan_number);
    }

    // Auto-fill loan amount
    if (loan.trans_details_loan_amt) {
      setLoanAmount(loan.trans_details_loan_amt);
    }

    // Auto-fill processor name
    const processorFullName = `${loan.loan_processor_first_name || ''} ${loan.loan_processor_last_name || ''}`.trim();
    if (processorFullName) {
      setProcessor(processorFullName);
    }

    // Auto-fill loan officer by matching encompass loan_officer_id to users
    // Priority: 1) Match by encompass_user_id, 2) Match by name
    let matchedOfficer = null;
    const loFirstName = loan.loan_officer_first_name?.toLowerCase().trim();
    const loLastName = loan.loan_officer_last_name?.toLowerCase().trim();
    const loEncompassId = loan.loan_officer_id;

    if (loanOfficers.length > 0) {
      // First priority: Match by Encompass user ID (most reliable)
      if (loEncompassId) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.encompassUserId === loEncompassId
        );
      }

      // Second priority: Exact name match
      if (!matchedOfficer && loFirstName && loLastName) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.firstName?.toLowerCase().trim() === loFirstName &&
          officer.lastName?.toLowerCase().trim() === loLastName
        );
      }

      // Third priority: Last name only match
      if (!matchedOfficer && loLastName) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.lastName?.toLowerCase().trim() === loLastName
        );
      }

      // Fourth priority: Partial match
      if (!matchedOfficer && loLastName) {
        matchedOfficer = loanOfficers.find(officer =>
          officer.lastName?.toLowerCase().includes(loLastName) ||
          loLastName.includes(officer.lastName?.toLowerCase() || '')
        );
      }

      if (matchedOfficer) {
        setLoanOfficerId(matchedOfficer.id);

        // The funded-date effect supplies the split; never read today's user
        // summary for a loan that may have funded under an earlier schedule.

        // Resolve mentor data for matched officer
        resolveMentorData(matchedOfficer);
      } else {
        setMentorData(null);
      }
    }

    // Auto-fill lender by matching encompass broker/lender name to investors
    if (loan.file_contacts_broker_lender_name && investors.length > 0) {
      const lenderName = loan.file_contacts_broker_lender_name.toLowerCase();
      const matchedInvestor = investors.find(inv =>
        inv.investor_name.toLowerCase().includes(lenderName) ||
        lenderName.includes(inv.investor_name.toLowerCase())
      );
      if (matchedInvestor) {
        setLender(matchedInvestor.id);
      }
    }

    // Estimated closing is not funding. Leave this blank until a real funded
    // date is available from Encompass or explicitly entered by the reviewer.
    setFundedDate(brokerFundingDate(loan.funded_date));

    toast.success('Loan linked', {
      description: `Connected to Encompass loan ${loan.trans_details_loan_number || ''}`
    });
  }, [loanOfficers, investors, pendingRecons, handleLoadPendingRecon, resolveMentorData]);

  // Handle clearing loan selection - reset ALL form state completely
  const handleClearLoan = useCallback(() => {
    // Clear loan selection
    setSelectedLoan(null);

    // Clear all form fields
    setBorrowerName('');
    setLoanNumber('');
    setLoanAmount(0);
    setLoanOfficerId('');
    setProcessor('');
    setFundedDate('');
    setLender('');
    setBrokerSplit(85); // Reset to default
    brokerSplitResolutionRequestRef.current += 1;
    setBrokerSplitBasis(null);
    setBrokerSplitResolving(false);
    setBrokerSplitResolutionError(null);

    // Clear fees and financial data
    setLenderFees([]);
    setGrossCheckReceived(0);
    setGrossCheckInput('');

    // Clear documents
    setUploadedDocuments([]);

    // Reset finalization state
    setCurrentPayoutStatus('pending');
    setCurrentPayoutId(null);

    // Clear mentor state
    setMentorData(null);

    toast.info('Form cleared - ready for new reconciliation');
  }, []);

  // Lazy-load approved/paid payouts when switching to finalized view
  useEffect(() => {
    if (viewMode !== 'finalized' || !user?.organization_id) {
return;
}
    const fetchApproved = async () => {
      setApprovedReconsLoading(true);
      const { data } = await supabase
        .from('payouts')
        .select('*, recipient:recipient_id(first_name, last_name), lender:lender_id(investor_name)')
        .eq('organization_id', user.organization_id)
        .eq('source_type', 'broker-recon')
        .in('status', ['approved', 'paid'])
        .neq('payout_type', 'mentor')
        .order('finalized_at', { ascending: false, nullsFirst: false });
      setApprovedRecons(data || []);
      setApprovedReconsLoading(false);
    };
    fetchApproved();
  }, [viewMode, user?.organization_id]);

  // Load a finalized payout into the form (read-only)
  const handleViewFinalizedPayout = async (payout: any) => {
    // 1. Rehydrate EncompassLoan with proper scoping
    let encompassLoan: EncompassLoan | null = null;
    if (payout.encompass_loan_id && user?.organization_id) {
      const { data } = await supabase
        .from('encompass_loans')
        .select(`
          id,
          encompass_loan_id,
          trans_details_loan_number,
          borrower_first_name,
          borrower_last_name,
          trans_details_loan_amt,
          trans_details_interest_rate,
          proposed_pi_payment,
          loan_term_months,
          loan_officer_id,
          loan_officer_first_name,
          loan_officer_last_name,
          loan_processor_id,
          loan_processor_first_name,
          loan_processor_last_name,
          file_contacts_broker_lender_name,
          closing_date,
          funded_date,
          milestone,
          trans_details_loan_status,
          loan_folder,
          fees_processing,
          fees_underwriting,
          fees_prepaid_interest,
          rate_lock_total_buy_price,
          rate_lock_buy_side_adjustments,
          le_fees,
          rate_lock_loan_program,
          first_payment_date,
          fees_initial_escrow_deposit,
          trans_details_non_specific_lender_credit,
          cure_applied_lender_credit,
          lender_credit_draft_amount,
          lender_credit_status
        `)
        .eq('encompass_loan_id', payout.encompass_loan_id)
        .eq('organization_id', user.organization_id)
        .eq('deleted_from_encompass', false)
        .single();
      if (data) {
encompassLoan = data as EncompassLoan;
}
    }

    // 2. Clear ALL stale state before loading
    setUploadedDocuments([]);
    setLenderFees([]);
    setGrossCheckInput('');
    setMentorData(null);
    setCurrentPayoutId(null);
    setCurrentPayoutStatus('pending');
    brokerSplitResolutionRequestRef.current += 1;
    setBrokerSplitBasis(null);
    setBrokerSplitResolving(false);
    setBrokerSplitResolutionError(null);

    // 3. Set loan context
    setSelectedLoan(encompassLoan);
    if (!encompassLoan && payout.encompass_loan_id) {
      toast.warning('Encompass loan not found', {
        description: 'Loan may have been deleted or moved. Data loaded from saved payout.'
      });
    }

    // 4. Switch to form view
    setViewMode('new');

    // 5. Load payout data (sets currentPayoutStatus via handleLoadPendingRecon)
    setTimeout(() => handleLoadPendingRecon(payout), 0);
  };

  // Reopen an approved payout back to pending/draft
  const handleReopenPayout = async () => {
    if (!currentPayoutId || !user?.organization_id || !loanNumber) {
return;
}

    // 1. Loan-level guard: check if ANY broker-recon payout for this loan is paid
    const { data: allLoanPayouts } = await supabase.from('payouts')
      .select('id, status, payout_type')
      .eq('loan_number', loanNumber)
      .eq('organization_id', user.organization_id)
      .eq('source_type', 'broker-recon');

    const hasPaidPayout = allLoanPayouts?.some(p => p.status === 'paid');
    if (hasPaidPayout) {
      toast.error('Cannot reopen', {
        description: 'One or more payouts for this loan have already been paid in compensation.'
      });
      return;
    }

    // 2. Revert LO payout status (finalized_at NOT cleared — persistent marker)
    const { data: updated, error } = await supabase.from('payouts')
      .update({
        status: 'pending',
        updated_at: new Date().toISOString()
      })
      .eq('id', currentPayoutId)
      .eq('status', 'approved')
      .select('id');

    if (error) {
      toast.error('Failed to reopen', { description: error.message });
      return;
    }

    if (!updated || updated.length === 0) {
      toast.error('Cannot reopen', {
        description: 'Payout status has changed since this page loaded. Please refresh and try again.'
      });
      return;
    }

    // 3. Revert companion mentor payout if exists (finalized_at NOT cleared)
    const { error: mentorError } = await supabase.from('payouts')
      .update({
        status: 'pending',
        updated_at: new Date().toISOString()
      })
      .eq('loan_number', loanNumber)
      .eq('organization_id', user.organization_id)
      .eq('payout_type', 'mentor')
      .eq('source_type', 'broker-recon')
      .eq('status', 'approved');

    if (mentorError) {
      // Roll back LO payout to approved (restore consistency)
      const { error: rollbackError } = await supabase.from('payouts')
        .update({ status: 'approved', updated_at: new Date().toISOString() })
        .eq('id', currentPayoutId);
      if (rollbackError) {
        toast.error('Failed to reopen — inconsistent state', {
          description: 'LO payout was reverted but mentor payout was not, and rollback failed. Please contact an administrator.'
        });
      } else {
        toast.error('Failed to reopen', {
          description: 'Mentor payout could not be reverted. No changes were made.'
        });
      }
      return;
    }

    // 4. Update local state
    setCurrentPayoutStatus('pending');

    // 5. Reload pending list
    await reloadPendingRecons();

    toast.success('Reconciliation reopened', {
      description: 'Status reverted to draft. Payout is now editable.'
    });
  };

  // NEW: Single smart document upload handler
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
return;
}

    // Check for duplicate
    const isDuplicate = uploadedDocuments.some(doc => (doc.fileName || doc.file?.name) === file.name);
    if (isDuplicate) {
      toast.error('Document already uploaded', {
        description: `${file.name} is already in the list`
      });
      return;
    }

    if (!selectedLoan?.encompass_loan_id) {
      toast.error('Select a loan before uploading documents', {
        description: 'The document must be bound to an authorized Encompass loan.'
      });
      e.target.value = '';
      return;
    }

    setUploading(true);
    const documentId = `doc-${Date.now()}`;

    try {
      if (!user?.organization_id) {
        throw new Error('No organization ID found');
      }

      // Step 1: Upload to Supabase Storage
      const fileName =
        `${user.organization_id}/${selectedLoan.encompass_loan_id}/`
        + `doc-${Date.now()}.pdf`;
      await reserveLoanStorageObject(
        fileName,
        selectedLoan.encompass_loan_id
      );
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('loan-documents')
        .upload(fileName, file);

      if (uploadError) {
throw uploadError;
}

      toast.success('File uploaded successfully');

      // Step 2: Queue for GPU worker extraction (uses local Docling)
      toast.info('Queuing for text extraction...');
      const queueId = crypto.randomUUID();
      const { error: queueError } = await supabase
        .from('document_processing_queue')
        .insert({
          id: queueId,
          organization_id: user.organization_id,
          user_id: user.id,
          encompass_loan_id: selectedLoan.encompass_loan_id,
          storage_path: uploadData.path,
          source_type: 'broker-recon',
          status: 'pending',
          file_size_bytes: file.size
        });

      if (queueError) {
throw queueError;
}

      // Add to uploaded documents with queued status
      const newDoc: UploadedDocument = {
        id: documentId,
        file,
        documentType: 'unknown',
        confidence: 0,
        storagePath: uploadData.path,
        uploadedAt: new Date(),
        needsClassification: false, // Will be handled by processingStatus
        queueId, // Track the queue ID for polling
        progress: 0,
        processingStatus: 'queued'
      };
      setUploadedDocuments(prev => [...prev, newDoc]);

      // Step 3: Subscribe to queue updates for this document
      const channel = supabase
        .channel(`broker-recon-${queueId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'document_processing_queue',
            filter: `id=eq.${queueId}`
          },
          async (payload) => {
            const queueItem = payload.new as { status: string; extracted_text: string | null; error_message: string | null; progress: number };

            // Accept both 'extracted' (new worker) and 'completed' (old worker) statuses
            // This ensures compatibility when GPU2 worker hasn't been restarted with the new code
            if ((queueItem.status === 'extracted' || queueItem.status === 'completed') && queueItem.extracted_text) {
              // Text extracted by GPU worker - now call Claude for classification (starts at 50%)
              setUploadedDocuments(prev => prev.map(doc =>
                doc.queueId === queueId
                  ? { ...doc, progress: 50, processingStatus: 'classifying' as const }
                  : doc
              ));
              toast.info('Classifying document with AI...');
              const classificationStartTime = Date.now();

              try {
                const { data: extractionData, error: extractionError } = await supabase.functions
                  .invoke('broker-recon-extract', {
                    body: {
                      extractedText: queueItem.extracted_text,
                      // PDF splitting parameters for combined documents
                      splitPdf: true,
                      storagePath: uploadData.path,
                      organizationId: user.organization_id,
                      loanNumber: loanNumber || `broker-recon-${Date.now()}`
                    }
                  });

                if (extractionError) {
throw extractionError;
}

                // Handle classification result
                if (extractionData?.needsClassification) {
                  setUploadedDocuments(prev => prev.map(doc =>
                    doc.id === documentId
                      ? {
                          ...doc,
                          documentType: extractionData.classification.documentType,
                          confidence: extractionData.classification.confidence,
                          needsClassification: true,
                          classificationReasoning: extractionData.classification.reasoning,
                          processingStatus: 'complete' as const, // Processing done, just low confidence
                          progress: 100
                        }
                      : doc
                  ));
                  toast.warning('Could not identify document type', {
                    description: extractionData.message
                  });
                } else if (extractionData?.success) {
                  const { documentType: detectedType, fields, classification, isCombined, settlementStatement, finalCheck } = extractionData;

                  // Handle combined document - creates two virtual document entries
                  if (isCombined && settlementStatement && finalCheck) {
                    // Update original document as combined
                    setUploadedDocuments(prev => {
                      const updated = prev.map(doc =>
                        doc.id === documentId
                          ? {
                              ...doc,
                              documentType: 'combined' as const,
                              confidence: classification?.confidence || 0.9,
                              extractedFields: {
                                settlementStatement,
                                finalCheck,
                                splitResult: extractionData.splitResult // Include split file paths
                              },
                              needsClassification: false,
                              classificationReasoning: classification?.reasoning,
                              isCombined: true,
                              processingStatus: 'complete' as const,
                              progress: 100,
                              splitError: describeSplitFailure(
                                extractionData.splitResult,
                                extractionData.splitError
                              )
                            }
                          : doc
                      );
                      return updated;
                    });


                    // Auto-populate form with settlement data
                    if (settlementStatement.borrowerName) {
setBorrowerName(settlementStatement.borrowerName);
}
                    // Only set loan number if not already set (preserve Encompass loan number)
                    if (settlementStatement.loanNumber && !loanNumber) {
setLoanNumber(settlementStatement.loanNumber);
}
                    if (settlementStatement.loanAmount) {
setLoanAmount(safeParseFloat(settlementStatement.loanAmount));
}
                    if (settlementStatement.fundedDate) {
setFundedDate(settlementStatement.fundedDate);
}

                    if (settlementStatement.lender && investors.length > 0) {
                      const matchedInvestor = investors.find(inv =>
                        inv.investor_name.toLowerCase().includes(settlementStatement.lender.toLowerCase()) ||
                        settlementStatement.lender.toLowerCase().includes(inv.investor_name.toLowerCase())
                      );
                      if (matchedInvestor) {
setLender(matchedInvestor.id);
}
                    }

                    const settlementConfidence = safeParseFloat(settlementStatement.confidence);
                    if (settlementConfidence < FEE_AUTO_APPLY_MIN_CONFIDENCE) {
                      // Warn even when the extractor returned zero fees — a
                      // dropped merged-row fee leaves an empty list behind.
                      toast.warning('Lender fees not auto-filled (low extraction confidence)', {
                        description: `Extraction confidence ${Math.round(settlementConfidence * 100)}% — verify the document and enter Lendwise fees manually.`
                      });
                    } else if (settlementStatement.lenderFees?.length > 0) {
                      const mappedFees = settlementStatement.lenderFees.map((fee: any, index: number) => ({
                        id: `extracted-${Date.now()}-${index}`,
                        name: fee.name || '',
                        totalFee: safeParseFloat(fee.totalFee),
                        borrowerPAC: safeParseFloat(fee.borrowerPAC),
                        borrowerPOC: safeParseFloat(fee.borrowerPOC),
                        lenderPOC: safeParseFloat(fee.lenderPOC),
                        reimbursement: safeParseFloat(fee.reimbursement),
                        reimburseTo: fee.reimburseTo || 'NA'
                      }));
                      setLenderFees(mappedFees);
                    }

                    // Auto-populate check amount
                    if (finalCheck.checkAmount) {
                      const checkAmount = safeParseFloat(finalCheck.checkAmount);
                      if (checkAmount > 0) {
                        setGrossCheckReceived(checkAmount);
                      }
                    }

                    const splitMsg = extractionData.splitResult
                      ? 'PDF split into separate files.'
                      : '';
                    toast.success(
                      'Combined document detected!',
                      { description: `Both Settlement Statement and Broker Check extracted. Form auto-filled. ${splitMsg}`.trim() }
                    );
                  } else if (fields) {
                    // Single document type
                    setUploadedDocuments(prev => prev.map(doc =>
                      doc.id === documentId
                        ? {
                            ...doc,
                            documentType: detectedType,
                            confidence: classification?.confidence || fields.confidence || 0,
                            extractedFields: fields,
                            needsClassification: false,
                            classificationReasoning: classification?.reasoning,
                            processingStatus: 'complete' as const,
                            progress: 100
                          }
                        : doc
                    ));

                    // Auto-populate form based on document type
                    if (detectedType === 'settlement_statement') {
                      if (fields.borrowerName) {
setBorrowerName(fields.borrowerName);
}
                      // Only set loan number if not already set (preserve Encompass loan number)
                      if (fields.loanNumber && !loanNumber) {
setLoanNumber(fields.loanNumber);
}
                      if (fields.loanAmount) {
setLoanAmount(safeParseFloat(fields.loanAmount));
}
                      if (fields.fundedDate) {
setFundedDate(fields.fundedDate);
}

                      if (fields.lender && investors.length > 0) {
                        const matchedInvestor = investors.find(inv =>
                          inv.investor_name.toLowerCase().includes(fields.lender.toLowerCase()) ||
                          fields.lender.toLowerCase().includes(inv.investor_name.toLowerCase())
                        );
                        if (matchedInvestor) {
setLender(matchedInvestor.id);
}
                      }

                      const settlementConfidence = safeParseFloat(fields.confidence);
                      if (settlementConfidence < FEE_AUTO_APPLY_MIN_CONFIDENCE) {
                        // Warn even when the extractor returned zero fees — a
                        // dropped merged-row fee leaves an empty list behind.
                        toast.warning('Lender fees not auto-filled (low extraction confidence)', {
                          description: `Extraction confidence ${Math.round(settlementConfidence * 100)}% — verify the document and enter Lendwise fees manually.`
                        });
                      } else if (fields.lenderFees && Array.isArray(fields.lenderFees) && fields.lenderFees.length > 0) {
                        const mappedFees = fields.lenderFees.map((fee: any, index: number) => ({
                          id: `extracted-${Date.now()}-${index}`,
                          name: fee.name || '',
                          totalFee: safeParseFloat(fee.totalFee),
                          borrowerPAC: safeParseFloat(fee.borrowerPAC),
                          borrowerPOC: safeParseFloat(fee.borrowerPOC),
                          lenderPOC: safeParseFloat(fee.lenderPOC),
                          reimbursement: safeParseFloat(fee.reimbursement),
                          reimburseTo: fee.reimburseTo || 'NA'
                        }));
                        setLenderFees(mappedFees);
                      }

                      const confidencePercent = Math.round((fields.confidence || 0) * 100);
                      toast.success(
                        `Settlement Statement detected! (${confidencePercent}% confidence)`,
                        { description: 'Form auto-filled with extracted data' }
                      );
                    } else if (detectedType === 'final_check') {
                      if (fields.checkAmount) {
                        const checkAmount = safeParseFloat(fields.checkAmount);
                        if (checkAmount > 0) {
                          setGrossCheckReceived(checkAmount);
                        }
                      }

                      const confidencePercent = Math.round((fields.confidence || 0) * 100);
                      toast.success(
                        `Final Check detected! (${confidencePercent}% confidence)`,
                        {
                          description: `Amount: $${fields.checkAmount || 'N/A'} | Check #: ${fields.checkNumber || 'N/A'}. Gross check field auto-filled.`
                        }
                      );
                    }
                  }

                  // Mark queue job as completed after successful classification
                  // Save classification results to database for debugging and audit trail
                  await supabase
                    .from('document_processing_queue')
                    .update({
                      status: 'completed',
                      progress: 100,
                      completed_at: new Date().toISOString(),
                      classification_result: extractionData,
                      split_result: extractionData.splitResult || null,
                      classification_time_ms: Date.now() - classificationStartTime
                    })
                    .eq('id', queueId);
                }
              } catch (classifyError) {
                console.error('Classification failed:', classifyError);
                setUploadedDocuments(prev => prev.map(doc =>
                  doc.queueId === queueId
                    ? { ...doc, processingStatus: 'failed' as const }
                    : doc
                ));
                toast.error('Classification failed', {
                  description: 'Text extracted but AI classification failed'
                });
              }

              // Unsubscribe after processing
              supabase.removeChannel(channel);
            } else if (queueItem.status === 'failed') {
              setUploadedDocuments(prev => prev.map(doc =>
                doc.queueId === queueId
                  ? { ...doc, processingStatus: 'failed' as const }
                  : doc
              ));
              toast.error('Extraction failed', {
                description: queueItem.error_message || 'GPU worker failed to extract text'
              });
              supabase.removeChannel(channel);
            } else {
              // Still extracting - update progress (extraction is 0-50%)
              // Only update if we haven't already progressed to classifying/complete/failed
              setUploadedDocuments(prev => prev.map(doc => {
                if (doc.queueId !== queueId) {
return doc;
}
                // Don't regress from classifying/complete/failed back to extracting
                if (doc.processingStatus === 'classifying' || doc.processingStatus === 'complete' || doc.processingStatus === 'failed') {
                  return doc;
                }
                return { ...doc, progress: Math.round((queueItem.progress || 0) / 2), processingStatus: 'extracting' as const };
              }));
            }
          }
        )
        .subscribe();

      toast.info('Document queued for GPU extraction', {
        description: 'GPU worker will extract text, then AI will classify'
      });

    } catch (error) {
      console.error('Document upload/extraction failed:', error);
      toast.error('Upload failed', {
        description: (error as Error).message || 'You can still enter data manually'
      });
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Remove uploaded document
  const handleRemoveDocument = (documentId: string) => {
    setUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
    toast.info('Document removed');
  };

  // Start renaming a document
  const handleStartRename = (doc: UploadedDocument) => {
    setRenamingDocId(doc.id);
    setRenameValue(doc.fileName || doc.file?.name || 'Document');
  };

  // Save renamed document
  const handleSaveRename = (documentId: string) => {
    if (renameValue.trim()) {
      setUploadedDocuments(prev =>
        prev.map(d => d.id === documentId ? { ...d, fileName: renameValue.trim() } : d)
      );
      toast.success('Document renamed');
    }
    setRenamingDocId(null);
    setRenameValue('');
  };

  // Cancel renaming
  const handleCancelRename = () => {
    setRenamingDocId(null);
    setRenameValue('');
  };

  // Start renaming a split file
  const handleStartRenameSplit = (docId: string, fileType: 'original' | 'settlement' | 'check', currentName: string) => {
    setRenamingSplitFile(`${docId}:${fileType}`);
    setSplitRenameValue(currentName);
  };

  // Save renamed split file
  const handleSaveRenameSplit = (docId: string, fileType: 'original' | 'settlement' | 'check') => {
    if (splitRenameValue.trim()) {
      setUploadedDocuments(prev =>
        prev.map(d => {
          if (d.id !== docId) {
return d;
}
          const key = fileType === 'original' ? 'original'
            : fileType === 'settlement' ? 'settlementStatement'
            : 'brokerCheck';
          return {
            ...d,
            splitFileNames: {
              ...d.splitFileNames,
              [key]: splitRenameValue.trim()
            }
          };
        })
      );
      toast.success('File renamed');
    }
    setRenamingSplitFile(null);
    setSplitRenameValue('');
  };

  // Cancel split file renaming
  const handleCancelRenameSplit = () => {
    setRenamingSplitFile(null);
    setSplitRenameValue('');
  };

  // Delete a split file from storage
  const handleDeleteSplitFile = async (docId: string, storagePath: string, fileType: 'original' | 'settlement' | 'check') => {
    try {
      // Delete from storage
      const { error } = await supabase.storage
        .from('loan-documents')
        .remove([storagePath]);

      if (error) {
throw error;
}

      // Update document state
      setUploadedDocuments(prev => {
        return prev.map(d => {
          if (d.id !== docId) {
return d;
}

          if (fileType === 'original') {
            // Deleting original - remove entire document
            return null;
          }

          // Update split result to remove the deleted path
          const splitResult = d.extractedFields?.splitResult;
          if (!splitResult) {
return d;
}

          const newSplitResult = { ...splitResult };
          if (fileType === 'settlement') {
            delete newSplitResult.settlementStatementPath;
          } else if (fileType === 'check') {
            delete newSplitResult.brokerCheckPath;
          }

          // If both split files are deleted, remove entire document
          if (!newSplitResult.settlementStatementPath && !newSplitResult.brokerCheckPath) {
            return null;
          }

          return {
            ...d,
            extractedFields: {
              ...d.extractedFields,
              splitResult: newSplitResult
            }
          };
        }).filter(Boolean) as UploadedDocument[];
      });

      toast.success('File deleted');
    } catch (err) {
      console.error('Error deleting file:', err);
      toast.error('Failed to delete file', {
        description: (err as Error).message
      });
    }
  };

  // View/download a document from storage
  // Retry splitting an already-uploaded combined document.
  //
  // Re-selecting the same PDF is refused by the filename duplicate check, so a
  // failed split needs its own retry that reuses the uploaded object and the
  // text the GPU worker already extracted.
  const handleRetrySplit = async (documentId: string) => {
    const doc = uploadedDocuments.find(d => d.id === documentId);
    if (!doc?.storagePath || !user?.organization_id) {
      return;
    }
    setUploadedDocuments(prev =>
      prev.map(d => d.id === documentId ? { ...d, retryingSplit: true } : d)
    );

    try {
      // A recon restored from a saved payout has no queue id — extraction
      // metadata does not persist one — so recover it from the storage path
      // that the payout does store. Without this, reopening a recon leaves a
      // failed split with no way back.
      const queueQuery = supabase
        .from('document_processing_queue')
        .select('extracted_text')
        .eq('organization_id', user.organization_id);

      const { data: queueRow, error: queueError } = doc.queueId
        ? await queueQuery.eq('id', doc.queueId).maybeSingle()
        : await queueQuery
            .eq('storage_path', doc.storagePath)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

      if (queueError) {
        throw queueError;
      }
      if (!queueRow?.extracted_text) {
        throw new Error('Extracted text is no longer available. Re-upload the combined document to split it again.');
      }

      const { data: retryData, error: retryError } = await supabase.functions
        .invoke('broker-recon-extract', {
          body: {
            extractedText: queueRow.extracted_text,
            splitPdf: true,
            storagePath: doc.storagePath,
            organizationId: user.organization_id,
            loanNumber: loanNumber || `broker-recon-${Date.now()}`
          }
        });

      if (retryError) {
        throw retryError;
      }

      const splitResult = retryData?.splitResult;
      // An incomplete retry must keep the failure state. Clearing it on a
      // response that merely omits `splitError` would remove the Retry action
      // while Push stays unavailable, stranding the document.
      const remainingFailure = describeSplitFailure(
        splitResult,
        retryData?.splitError
      );

      setUploadedDocuments(prev =>
        prev.map(d => d.id === documentId
          ? {
              ...d,
              retryingSplit: false,
              splitError: remainingFailure,
              extractedFields: {
                ...d.extractedFields,
                ...(splitResult ? { splitResult } : {})
              }
            }
          : d)
      );

      if (remainingFailure) {
        toast.error('Split still incomplete', { description: remainingFailure });
      } else {
        toast.success('Split documents created');
      }
    } catch (error) {
      setUploadedDocuments(prev =>
        prev.map(d => d.id === documentId
          ? { ...d, retryingSplit: false, splitError: (error as Error).message }
          : d)
      );
      toast.error('Could not retry split', {
        description: (error as Error).message
      });
    }
  };

  const handleViewDocument = async (storagePath: string, fileName?: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('loan-documents')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
throw error;
}

      // Open in new tab
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      toast.error('Failed to open document', {
        description: (err as Error).message
      });
    }
  };

  // Rotate a document 90 degrees clockwise
  const handleRotateDocument = async (storagePath: string) => {
    try {
      toast.info('Rotating document...');

      // 1. Download PDF from storage
      const { data: downloadData, error: downloadError } = await supabase.storage
        .from('loan-documents')
        .download(storagePath);

      if (downloadError) {
throw downloadError;
}

      // 2. Load PDF and rotate all pages 90° clockwise
      const pdfBytes = await downloadData.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      const pages = pdfDoc.getPages();
      for (const page of pages) {
        const currentRotation = page.getRotation().angle;
        page.setRotation(degrees(currentRotation + 90));
      }

      // 3. Save and upload back to storage (overwrite)
      const rotatedBytes = await pdfDoc.save();
      const { error: uploadError } = await supabase.storage
        .from('loan-documents')
        .upload(storagePath, rotatedBytes, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (uploadError) {
throw uploadError;
}

      toast.success('Document rotated 90° clockwise');
    } catch (err) {
      console.error('Rotation error:', err);
      toast.error('Failed to rotate document', {
        description: (err as Error).message
      });
    }
  };

  // Save Draft - Persist to payouts with status='pending'
  const handleSaveDraft = async () => {
    if (!loanNumber) {
      toast.error('Loan number is required');
      return;
    }
    if (!loanOfficerId) {
      toast.error('Please select a Loan Officer');
      return;
    }
    if (!user?.organization_id) {
      toast.error('Organization not found');
      return;
    }
    if (!validateFundingDatedBrokerSplit()) {
      return;
    }

    try {
      // Find settlement statement and final check paths
      const settlementDoc = uploadedDocuments.find(d => d.documentType === 'settlement_statement');
      const checkDoc = uploadedDocuments.find(d => d.documentType === 'final_check');

      const payoutData = {
        organization_id: user.organization_id,
        recipient_id: loanOfficerId,
        source_officer_id: loanOfficerId,
        // Use Encompass field 364 as source of truth when loan is selected
        loan_number: selectedLoan?.trans_details_loan_number || loanNumber,
        borrower_name: borrowerName || null,
        lender_id: lender || null,
        processor: processor || null,
        loan_amount: loanAmount || 0,
        fund_date: fundedDate || null,

        // Check reconciliation
        gross_check_received: grossCheckReceived,
        check_variance: checkVariance,
        lender_poc_total: totalLenderPOC,
        lo_reimbursements_total: totalLOReimbursements,

        // Lender fees array (full detail for reload)
        lender_fees: lenderFees,

        // Fee deductions
        processing_fee: PROCESSING_FEE,
        e_and_o_deduction: loEAndO,
        broker_split_percent: brokerSplit,
        broker_split_history_id: brokerSplitHistoryId,
        broker_split_effective_date: brokerSplitEffectiveDate,

        // Commission breakdown (adjusted for mentor split if active)
        lo_commission_base: mentorData ? loCommissionAfterMentor : loCommissionBase,
        gross_amount: mentorData ? loTotalAfterMentor : loTotalCommission,
        net_amount: (mentorData ? loTotalAfterMentor : loTotalCommission) - loEAndO,
        company_commission_base: companyCommissionBase,
        company_total_revenue: companyTotalRevenue,

        // Document paths - check for split paths from combined documents first, then individual docs
        settlement_statement_path: (() => {
          // Check for split result from combined document
          const combinedDoc = uploadedDocuments.find(d => d.documentType === 'combined');
          const splitPath = combinedDoc?.extractedFields?.splitResult?.settlementStatementPath;
          if (splitPath) {
return splitPath;
}
          // Fallback to individual document
          return settlementDoc?.storagePath || null;
        })(),
        final_check_path: (() => {
          // Check for split result from combined document
          const combinedDoc = uploadedDocuments.find(d => d.documentType === 'combined');
          const splitPath = combinedDoc?.extractedFields?.splitResult?.brokerCheckPath;
          if (splitPath) {
return splitPath;
}
          // Fallback to individual document
          return checkDoc?.storagePath || null;
        })(),
        combined_document_path: (() => {
          const combinedDoc = uploadedDocuments.find(d => d.documentType === 'combined');
          return combinedDoc?.storagePath || null;
        })(),

        // Store Claude LLM extraction results for audit/debugging
        extraction_results: (() => {
          const results: Record<string, any> = {};
          uploadedDocuments.forEach(doc => {
            if (doc.extractedFields) {
              results[doc.documentType] = {
                storagePath: doc.storagePath,
                fileName: doc.fileName || doc.file?.name,
                confidence: doc.confidence,
                extractedAt: doc.uploadedAt,
                fields: doc.extractedFields
              };
            }
          });
          return Object.keys(results).length > 0 ? results : null;
        })(),

        // Metadata
        source_type: 'broker-recon',
        payout_type: 'brokered',
        payout_sequence: 1, // First payout for this loan/recipient
        status: 'pending',
        updated_at: new Date().toISOString()
      };

      // Upsert by loan_number + organization_id + recipient_id + payout_sequence
      const { error } = await supabase
        .from('payouts')
        .upsert(payoutData, {
          onConflict: 'loan_number,organization_id,recipient_id,payout_sequence',
          ignoreDuplicates: false
        });

      if (error) {
throw error;
}

      // Save/cleanup mentor payout
      const loanNum = selectedLoan?.trans_details_loan_number || loanNumber;
      if (mentorData) {
        // Clean up old mentor payout if mentor changed
        await supabase
          .from('payouts')
          .delete()
          .eq('loan_number', loanNum)
          .eq('organization_id', user.organization_id)
          .eq('payout_type', 'mentor')
          .eq('source_type', 'broker-recon')
          .neq('recipient_id', mentorData.mentorId);

        // Upsert mentor payout
        const { error: mentorError } = await supabase
          .from('payouts')
          .upsert({
            organization_id: user.organization_id,
            recipient_id: mentorData.mentorId,
            source_officer_id: loanOfficerId,
            loan_number: loanNum,
            borrower_name: borrowerName || null,
            lender_id: lender || null,
            processor: processor || null,
            loan_amount: loanAmount || 0,
            fund_date: fundedDate || null,
            gross_check_received: 0,
            check_variance: 0,
            lender_poc_total: 0,
            lo_reimbursements_total: totalMentorReimbursements,
            lender_fees: null,
            processing_fee: 0,
            e_and_o_deduction: mentorEAndO,
            broker_split_percent: mentorData.mentorSplitPercent,
            lo_commission_base: mentorCommission,
            gross_amount: mentorTotal,
            net_amount: mentorTotal - mentorEAndO,
            company_commission_base: 0,
            company_total_revenue: 0,
            source_type: 'broker-recon',
            payout_type: 'mentor',
            payout_sequence: 1,
            splits_description: `${mentorData.mentorSplitPercent}% mentor split from ${loanOfficers.find(o => o.id === loanOfficerId)?.fullName || 'LO'}`,
            status: 'pending',
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'loan_number,organization_id,recipient_id,payout_sequence',
            ignoreDuplicates: false
          });

        if (mentorError) {
          console.error('Failed to save mentor payout:', mentorError);
          toast.warning('LO draft saved, but mentor payout failed', {
            description: mentorError.message
          });
        }
      } else {
        // No mentor — clean up any orphaned mentor payouts for this loan
        await supabase
          .from('payouts')
          .delete()
          .eq('loan_number', loanNum)
          .eq('organization_id', user.organization_id)
          .eq('payout_type', 'mentor')
          .eq('source_type', 'broker-recon')
          .eq('status', 'pending');
      }

      // Reload pending list to show the saved recon
      await reloadPendingRecons();

      toast.success('Reconciliation saved as draft', {
        description: mentorData
          ? `LO: ${formatCurrency(loTotalAfterMentor)}, Mentor: ${formatCurrency(mentorTotal)}`
          : 'Saved to payouts with pending status'
      });
    } catch (error) {
      console.error('Save draft failed:', error);
      toast.error('Failed to save draft', {
        description: (error as Error).message
      });
    }
  };

  // Delete Payout - Removes payout record and associated storage files
  const handleDeletePayout = async (payoutId: string) => {
    try {
      // 1. Fetch the payout to get document paths and loan info
      const { data: payout, error: fetchError } = await supabase
        .from('payouts')
        .select('combined_document_path, settlement_statement_path, final_check_path, loan_number, organization_id')
        .eq('id', payoutId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch payout: ${fetchError.message}`);
      }

      if (payout) {
        // 2. Collect all paths to delete
        const pathsToDelete = [
          payout.combined_document_path,
          payout.settlement_statement_path,
          payout.final_check_path
        ].filter(Boolean) as string[];

        // 3. Delete files from storage
        if (pathsToDelete.length > 0) {
          const { error: storageError } = await supabase.storage
            .from('loan-documents')
            .remove(pathsToDelete);

          if (storageError) {
            console.error('Error deleting storage files:', storageError);
            // Continue with record deletion even if storage fails
          }
        }
      }

      // 4. Delete the payout record
      const { error: deleteError } = await supabase
        .from('payouts')
        .delete()
        .eq('id', payoutId);

      if (deleteError) {
        throw new Error(`Failed to delete payout: ${deleteError.message}`);
      }

      // 4b. Delete companion mentor payout if exists
      if (payout?.loan_number && payout?.organization_id) {
        await supabase
          .from('payouts')
          .delete()
          .eq('loan_number', payout.loan_number)
          .eq('organization_id', payout.organization_id)
          .eq('payout_type', 'mentor')
          .eq('source_type', 'broker-recon');
      }

      // 5. Refresh pending list
      await reloadPendingRecons();

      // 6. Clear current form if we deleted the loaded payout
      if (currentPayoutId === payoutId) {
        setCurrentPayoutId(null);
        setSelectedLoan(null);
        setBorrowerName('');
        setLoanNumber('');
        setLender('');
        setProcessor('');
        setLoanOfficerId('');
        setBrokerSplit(85);
        brokerSplitResolutionRequestRef.current += 1;
        setBrokerSplitBasis(null);
        setBrokerSplitResolving(false);
        setBrokerSplitResolutionError(null);
        setFundedDate('');
        setLoanAmount(0);
        setGrossCheckReceived(0);
        setLenderFees([]);
        setUploadedDocuments([]);
        setMentorData(null);
      }

      toast.success('Reconciliation deleted', {
        description: 'Payout record and associated documents removed'
      });
    } catch (error) {
      console.error('Delete payout failed:', error);
      toast.error('Failed to delete reconciliation', {
        description: (error as Error).message
      });
    }
  };

  // Push to Accounting - Saves and approves payout
  const handlePushToAccounting = async () => {
    // Validate reconciliation
    if (!isReconciled) {
      toast.error('Cannot push to accounting', {
        description: 'Check must be reconciled (balance = $0) before pushing'
      });
      return;
    }

    if (!loanNumber) {
      toast.error('Loan number is required');
      return;
    }

    if (!loanOfficerId) {
      toast.error('Please select a Loan Officer');
      return;
    }

    if (!user?.organization_id) {
      toast.error('Organization not found');
      return;
    }
    if (!validateFundingDatedBrokerSplit()) {
      return;
    }

    setPushingToAccounting(true);

    try {
      // FIRST: Update milestone to Completion in Encompass and move to Completed Loans folder
      // This ensures we don't create a payout record if milestone update fails
      if (selectedLoan?.encompass_loan_id) {
        // Build field writes for loan status + HMDA broker compensation
        const fieldWrites: Array<{ id: string; value: string }> = [
          { id: '1393', value: 'Loan Originated' },
          // HMDA: Broker Compensation (Line 801)
          { id: 'NEWHUD.X224', value: grossCheckReceived.toFixed(2) },
          { id: 'NEWHUD.X223', value: '' }  // Clear any stale percentage
        ];
        if (fundedDate) {
          // Convert YYYY-MM-DD → MM/DD/YYYY for Encompass
          const [y, m, d] = fundedDate.split('-');
          const usDate = `${m}/${d}/${y}`;
          fieldWrites.push({ id: '748', value: usDate });
          fieldWrites.push({ id: '749', value: usDate });
        }

        const { data: milestoneData, error: milestoneError } = await supabase.functions.invoke('update-encompass-milestone', {
          body: {
            encompassLoanId: selectedLoan.encompass_loan_id,
            milestoneName: 'Completion',
            milestoneDate: fundedDate ? `${fundedDate}T00:00:00Z` : undefined,
            moveToFolder: 'Completed Loans',
            fieldWrites,
            requireFieldWritesSuccess: true,
            sourceType: 'broker_recon',
            sourceId: currentPayoutId || undefined
          }
        });

        if (milestoneError) {
          // Extract actual error from edge function response body
          let detail = milestoneError.message;
          try {
            const body = await (milestoneError as any).context?.json?.();
            if (body?.error) {
detail = body.error;
}
          } catch { /* response body unavailable */ }
          throw new Error(`Failed to update milestone: ${detail}`);
        }

        if (milestoneData?.error) {
          throw new Error(milestoneData.error);
        }

        // HMDA field writes are required — block payout if they failed
        if (milestoneData?.fieldWriteError) {
          throw new Error(`HMDA field write failed: ${milestoneData.fieldWriteError}`);
        }
        if (milestoneData?.fieldsWritten !== undefined && milestoneData.fieldsWritten < fieldWrites.length) {
          throw new Error(`HMDA field write incomplete: ${milestoneData.fieldsWritten}/${fieldWrites.length} fields written`);
        }

        // Edge function now writes loan_folder directly — consume folderMoveStatus
        if (milestoneData?.folderMoveStatus === 'moved') {
          // Success — no client-side DB update needed
        } else if (milestoneData?.folderMoveStatus === 'queued_retry') {
          toast.info('Folder Move Queued', {
            description: 'Loan folder move queued for automatic retry.'
          });
        } else if (milestoneData?.folderMoveStatus === 'failed_terminal') {
          toast.error('Folder Move Failed', {
            description: `Could not move loan to Completed Loans${milestoneData.folderError ? `: ${milestoneData.folderError}` : ''} — please move manually in Encompass.`
          });
        }
      }

      // Find settlement statement and final check paths
      const settlementDoc = uploadedDocuments.find(d => d.documentType === 'settlement_statement');
      const checkDoc = uploadedDocuments.find(d => d.documentType === 'final_check');

      // Check if this payout was ever finalized before (for mentorship double-count prevention)
      // Must query BEFORE the upsert overwrites finalized_at
      const approvedLoanNumForCheck = selectedLoan?.trans_details_loan_number || loanNumber;
      const { data: priorPayout } = await supabase.from('payouts')
        .select('finalized_at')
        .eq('loan_number', approvedLoanNumForCheck)
        .eq('organization_id', user.organization_id)
        .eq('recipient_id', loanOfficerId)
        .eq('source_type', 'broker-recon')
        .eq('payout_type', 'brokered')
        .maybeSingle();
      const wasEverFinalized = !!priorPayout?.finalized_at;

      // SECOND: Upsert payout with approved status (only after milestone succeeds)
      const { error } = await supabase
        .from('payouts')
        .upsert({
          organization_id: user.organization_id,
          recipient_id: loanOfficerId,
          source_officer_id: loanOfficerId,
          // Use Encompass field 364 as source of truth when loan is selected
          loan_number: selectedLoan?.trans_details_loan_number || loanNumber,
          borrower_name: borrowerName || null,
          lender_id: lender || null,
          processor: processor || null,
          loan_amount: loanAmount || 0,
          fund_date: fundedDate || null,
          encompass_loan_id: selectedLoan?.encompass_loan_id || null,

          // Check reconciliation
          gross_check_received: grossCheckReceived,
          check_variance: checkVariance,
          lender_poc_total: totalLenderPOC,
          lo_reimbursements_total: totalLOReimbursements,

          // Lender fees array (full detail for reload)
          lender_fees: lenderFees,

          // Fee deductions
          processing_fee: PROCESSING_FEE,
          e_and_o_deduction: loEAndO,
          broker_split_percent: brokerSplit,
          broker_split_history_id: brokerSplitHistoryId,
          broker_split_effective_date: brokerSplitEffectiveDate,

          // Commission breakdown (adjusted for mentor split if active)
          lo_commission_base: mentorData ? loCommissionAfterMentor : loCommissionBase,
          gross_amount: mentorData ? loTotalAfterMentor : loTotalCommission,
          net_amount: (mentorData ? loTotalAfterMentor : loTotalCommission) - loEAndO,
          company_commission_base: companyCommissionBase,
          company_total_revenue: companyTotalRevenue,

          // Document paths - check for split paths from combined documents first, then individual docs
          settlement_statement_path: (() => {
            const combinedDoc = uploadedDocuments.find(d => d.documentType === 'combined');
            const splitPath = combinedDoc?.extractedFields?.splitResult?.settlementStatementPath;
            return splitPath || settlementDoc?.storagePath || null;
          })(),
          final_check_path: (() => {
            const combinedDoc = uploadedDocuments.find(d => d.documentType === 'combined');
            const splitPath = combinedDoc?.extractedFields?.splitResult?.brokerCheckPath;
            return splitPath || checkDoc?.storagePath || null;
          })(),
          combined_document_path: (() => {
            const combinedDoc = uploadedDocuments.find(d => d.documentType === 'combined');
            return combinedDoc?.storagePath || null;
          })(),

          // Store Claude LLM extraction results for audit/debugging
          extraction_results: (() => {
            const results: Record<string, any> = {};
            uploadedDocuments.forEach(doc => {
              if (doc.extractedFields) {
                results[doc.documentType] = {
                  storagePath: doc.storagePath,
                  fileName: doc.fileName || doc.file?.name,
                  confidence: doc.confidence,
                  extractedAt: doc.uploadedAt,
                  fields: doc.extractedFields
                };
              }
            });
            return Object.keys(results).length > 0 ? results : null;
          })(),

          // Metadata
          source_type: 'broker-recon',
          payout_type: 'brokered',
          payout_sequence: 1, // First payout for this loan/recipient
          splits_description: mentorData ? `${brokerSplit}% (less ${mentorSplitPercent}% mentor)` : `${brokerSplit}%`,
          status: 'approved', // Approved = ready for payment
          finalized_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'loan_number,organization_id,recipient_id,payout_sequence',
          ignoreDuplicates: false
        });

      if (error) {
throw error;
}

      // Approve mentor payout if active
      const approvedLoanNum = selectedLoan?.trans_details_loan_number || loanNumber;
      if (mentorData) {
        const { error: mentorError } = await supabase
          .from('payouts')
          .upsert({
            organization_id: user.organization_id,
            recipient_id: mentorData.mentorId,
            source_officer_id: loanOfficerId,
            loan_number: approvedLoanNum,
            borrower_name: borrowerName || null,
            lender_id: lender || null,
            processor: processor || null,
            loan_amount: loanAmount || 0,
            fund_date: fundedDate || null,
            encompass_loan_id: selectedLoan?.encompass_loan_id || null,
            gross_check_received: 0,
            check_variance: 0,
            processing_fee: 0,
            e_and_o_deduction: mentorEAndO,
            broker_split_percent: mentorData.mentorSplitPercent,
            lo_commission_base: mentorCommission,
            gross_amount: mentorTotal,
            net_amount: mentorTotal - mentorEAndO,
            company_commission_base: 0,
            company_total_revenue: 0,
            source_type: 'broker-recon',
            payout_type: 'mentor',
            payout_sequence: 1,
            splits_description: `${mentorData.mentorSplitPercent}% mentor split`,
            status: 'approved',
            finalized_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'loan_number,organization_id,recipient_id,payout_sequence',
            ignoreDuplicates: false
          });

        if (mentorError) {
throw mentorError;
}

        // Increment brokered_deals_completed (only on FIRST approval, not reopened re-pushes)
        // wasEverFinalized was queried BEFORE the upsert to avoid race conditions
        if (!wasEverFinalized) {
          const { data: currentLO } = await supabase
            .from('users')
            .select('brokered_deals_completed, mentorship_active')
            .eq('id', loanOfficerId)
            .single();

          const currentCount = currentLO?.brokered_deals_completed ?? 0;
          if (currentCount < 5 && currentLO?.mentorship_active) {
            const newCount = currentCount + 1;
            // brokered_deals_completed is not a guarded comp column, but mentorship_active is.
            // Split the writes: direct .update for brokered_deals_completed, comp-set for mentorship_active.
            await supabase
              .from('users')
              .update({ brokered_deals_completed: newCount })
              .eq('id', loanOfficerId);

            if (newCount >= 5) {
              await compSet({ userId: loanOfficerId, mentorship_active: false });
              toast.info('Mentorship graduated!', {
                description: `${loanOfficers.find(o => o.id === loanOfficerId)?.fullName} has completed 5 brokered deals.`
              });
            }
          }
        }
      } else {
        // No mentor — clean up any orphaned mentor payouts for this loan
        await supabase
          .from('payouts')
          .delete()
          .eq('loan_number', approvedLoanNum)
          .eq('organization_id', user.organization_id)
          .eq('payout_type', 'mentor')
          .eq('source_type', 'broker-recon');
      }

      // Reload pending list to reflect status change
      await reloadPendingRecons();

      setCurrentPayoutStatus('approved');
      toast.success('Pushed to Accounting', {
        description: mentorData
          ? `LO: ${formatCurrency(loTotalAfterMentor)}, Mentor: ${formatCurrency(mentorTotal)} approved.`
          : `LO payout of ${formatCurrency(loTotalCommission)} approved. Milestone updated to Completion.`
      });

    } catch (error) {
      toast.error('Failed to push to accounting', {
        description: (error as Error).message
      });
    } finally {
      setPushingToAccounting(false);
    }
  };

  // Push document to Encompass
  const handlePushToEncompass = async (documentId: string) => {
    const doc = uploadedDocuments.find(d => d.id === documentId);
    if (!doc) {
return;
}

    // Validate prerequisites
    if (!selectedLoan?.encompass_loan_id) {
      toast.error('Please select a loan first', {
        description: 'A loan must be selected to push documents to Encompass'
      });
      return;
    }

    if (doc.documentType === 'unknown') {
      toast.error('Cannot push unknown document type', {
        description: 'Document must be classified as Settlement Statement or Final Check'
      });
      return;
    }

    if (!isReconciled) {
      toast.error('Reconciliation must balance first', {
        description: `Variance is ${formatCurrency(checkVariance)}. Gross Check must equal Total Fees before pushing to Encompass.`
      });
      return;
    }

    // Set loading state
    setUploadedDocuments(prev =>
      prev.map(d => d.id === documentId ? { ...d, pushingToEncompass: true } : d)
    );

    try {
      // Handle combined documents - push both split files
      if (doc.documentType === 'combined') {
        const splitResult = doc.extractedFields?.splitResult;
        if (!splitResult?.settlementStatementPath || !splitResult?.brokerCheckPath) {
          throw new Error('Combined document must be split before pushing to Encompass. Please wait for processing to complete.');
        }

        // Push settlement statement
        const { data: settlementData, error: settlementError } = await supabase.functions.invoke('push-to-encompass', {
          body: {
            storagePath: splitResult.settlementStatementPath,
            encompassLoanId: selectedLoan.encompass_loan_id,
            documentType: 'settlement_statement',
            organizationId: user?.organization_id
          }
        });
        if (settlementError) {
throw settlementError;
}

        // Push broker check
        const { data: checkData, error: checkError } = await supabase.functions.invoke('push-to-encompass', {
          body: {
            storagePath: splitResult.brokerCheckPath,
            encompassLoanId: selectedLoan.encompass_loan_id,
            documentType: 'final_check',
            organizationId: user?.organization_id
          }
        });
        if (checkError) {
throw checkError;
}

        // Update document state with success
        setUploadedDocuments(prev =>
          prev.map(d => d.id === documentId ? {
            ...d,
            pushingToEncompass: false,
            pushedToEncompass: true,
            pushedAt: new Date()
          } : d)
        );

        toast.success('Both documents pushed to Encompass', {
          description: 'Settlement Statement and Broker Check uploaded to loan file'
        });
        return;
      }

      // Single document - push directly
      const { data, error } = await supabase.functions.invoke('push-to-encompass', {
        body: {
          storagePath: doc.storagePath,
          encompassLoanId: selectedLoan.encompass_loan_id,
          documentType: doc.documentType,
          organizationId: user?.organization_id
        }
      });

      if (error) {
throw error;
}

      if (data?.success) {
        // Update document state with success
        setUploadedDocuments(prev =>
          prev.map(d => d.id === documentId ? {
            ...d,
            pushingToEncompass: false,
            pushedToEncompass: true,
            encompassAttachmentId: data.attachmentId,
            pushedAt: new Date()
          } : d)
        );

        toast.success('Document pushed to Encompass', {
          description: `"${data.documentName}" uploaded to loan file`
        });
      } else {
        throw new Error(data?.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Failed to push document to Encompass:', error);

      // Reset loading state
      setUploadedDocuments(prev =>
        prev.map(d => d.id === documentId ? { ...d, pushingToEncompass: false } : d)
      );

      toast.error('Failed to push to Encompass', {
        description: (error as Error).message || 'Please try again'
      });
    }
  };

  // Get document type display info
  const getDocumentTypeInfo = (type: string) => {
    switch (type) {
      case 'settlement_statement':
        return { icon: '📄', label: 'Settlement Statement', color: 'blue' };
      case 'final_check':
        return { icon: '💵', label: 'Final Check', color: 'green' };
      case 'combined':
        return { icon: '📑', label: 'Combined (Settlement + Check)', color: 'purple' };
      default:
        return { icon: '❓', label: 'Unknown', color: 'gray' };
    }
  };

  // Get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) {
return 'text-green-600 bg-green-50';
}
    if (confidence >= 0.6) {
return 'text-yellow-600 bg-yellow-50';
}
    return 'text-red-600 bg-red-50';
  };

  // Download broker recon as PDF
  const handlePrintPdf = () => {
    const lenderName = investors.find(i => i.id === lender)?.investor_name || 'Unknown Lender';
    const selectedOfficer = loanOfficers.find(o => o.id === loanOfficerId);
    const loanOfficerName = selectedOfficer
      ? `${selectedOfficer.firstName} ${selectedOfficer.lastName}`.trim()
      : 'Unknown';

    try {
      downloadBrokerReconPDF({
        loanNumber, borrowerName, fundedDate: fundedDate || null, loanAmount,
        lenderName, loanOfficerName, processor, companyName,
        brokerSplit, grossCheckReceived, totalFees, checkVariance, isReconciled,
        lenderFees,
        totalBorrowerPAC, totalBorrowerPOC, totalLenderPOC, totalReimbursement,
        baseCheck, processingFee: PROCESSING_FEE, eoInsurance: EO_INSURANCE,
        totalLOReimbursements, totalMentorReimbursements, netForSplit,
        loCommissionBase, loTotalCommission, loEAndO,
        companyCommissionBase, companyTotalRevenue,
        loTotalAfterMentor,
        mentorData: mentorData ? {
          mentorName: mentorData.mentorName,
          splitPercent: mentorSplitPercent,
          commission: mentorCommission,
          totalReimbursements: totalMentorReimbursements,
          total: mentorTotal,
          eAndO: mentorEAndO
        } : undefined
      });
    } catch (err) {
      toast.error('PDF generation failed', {
        description: err instanceof Error ? err.message : 'Please try again.'
      });
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground mb-2">BROKERED LOAN RECONCILIATION PAGE</h1>

        {/* View Mode Toggle */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <Button
            variant={viewMode === 'new' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('new')}
          >
            <FilePlus2 className="h-4 w-4 mr-2" />
            New Reconciliation
          </Button>
          <Button
            variant={viewMode === 'finalized' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('finalized')}
          >
            <ListChecks className="h-4 w-4 mr-2" />
            Finalized Records
          </Button>
        </div>

        {viewMode === 'new' && (isFinalized || isPaid) && fundedDate && (
          <Alert className="border-red-500 bg-red-50 dark:bg-red-950">
            <AlertDescription className="flex items-center justify-center gap-3 text-red-700 dark:text-red-400">
              <span className="font-semibold">This loan was marked as Completed {formatDateForDisplay(fundedDate)}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handlePrintPdf} title="Download broker recon PDF" aria-label="Download broker recon PDF">
                <Printer className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Finalized Records Table */}
      {viewMode === 'finalized' && (
        <FinalizedPayoutsTable
          payouts={approvedRecons}
          loading={approvedReconsLoading}
          onViewPayout={handleViewFinalizedPayout}
        />
      )}

      {/* Main Content Grid */}
      {viewMode === 'new' && (<>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Loan Details */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label>Select Loan:</Label>
              {user?.organization_id && !isReadOnly && (
                <LoanAutocomplete
                  organizationId={user.organization_id}
                  selectedLoan={selectedLoan}
                  onSelect={handleLoanSelect}
                  onClear={handleClearLoan}
                  includeCompleted
                />
              )}
              {selectedLoan && (
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Borrower: <span className="font-medium text-foreground">{borrowerName}</span></span>
                  {selectedLoan.milestone && (
                    <Badge variant="outline" className="text-xs">
                      {selectedLoan.milestone}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company:</Label>
                <Input
                  id="company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loan-number">Loan Number:</Label>
                <Input
                  id="loan-number"
                  value={loanNumber}
                  onChange={(e) => setLoanNumber(e.target.value)}
                  disabled={!!selectedLoan || isReadOnly}
                  className={selectedLoan || isReadOnly ? 'bg-muted' : ''}
                />
                {selectedLoan && (
                  <p className="text-xs text-muted-foreground">Locked from Encompass (Field 364)</p>
                )}
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Right Column - Financial Calculations */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="funded-date" className="text-blue-600 font-semibold">Funded Date:</Label>
                <Input
                  id="funded-date"
                  type="date"
                  value={fundedDate}
                  onChange={(e) => setFundedDate(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loan-amount" className="text-blue-600 font-semibold">Loan Amount:</Label>
                <Input
                  id="loan-amount"
                  type="text"
                  value={formatCurrency(loanAmount)}
                  onChange={(e) => {
                    const num = parseFloat(e.target.value.replace(/[^0-9.]/g, ''));
                    if (!isNaN(num)) {
setLoanAmount(num);
}
                  }}
                  className="text-right"
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lender" className="text-blue-600 font-semibold">Lender:</Label>
                <Select
                  value={lender}
                  onValueChange={setLender}
                  disabled={loadingInvestors || investors.length === 0 || isReadOnly}
                >
                  <SelectTrigger id="lender">
                    <SelectValue
                      placeholder={
                        loadingInvestors
                          ? "Loading..."
                          : investors.length === 0
                            ? "No wholesale investors found"
                            : "Select lender"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {investors.length === 0 ? (
                      <SelectItem value="none" disabled>No investors available</SelectItem>
                    ) : (
                      investors.map((investor) => (
                        <SelectItem key={investor.id} value={investor.id}>
                          {investor.investor_name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="processor">Processor:</Label>
                <Input
                  id="processor"
                  value={processor}
                  onChange={(e) => setProcessor(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="loan-officer">Loan Officer:</Label>
                <Select
                  value={loanOfficerId}
                  onValueChange={(officerId) => {
                    if (officerId === loanOfficerId) {
                      return;
                    }
                    setLoanOfficerId(officerId);
                    setBrokerSplitReassigned(true);
                    brokerSplitResolutionRequestRef.current += 1;
                    setBrokerSplitResolving(true);
                    setBrokerSplitResolutionError(null);
                    setBrokerSplitBasis(null);
                    // The effect resolves the new LO by this loan's funded date,
                    // including when explicitly reassigning an existing draft.
                    const selectedOfficer = loanOfficers.find(o => o.id === officerId);
                    // Resolve mentor data for selected officer
                    if (selectedOfficer) {
                      resolveMentorData(selectedOfficer);
                    } else {
                      setMentorData(null);
                    }
                  }}
                  disabled={loadingOfficers || loanOfficers.length === 0 || isReadOnly}
                >
                  <SelectTrigger id="loan-officer">
                    <SelectValue
                      placeholder={
                        loadingOfficers
                          ? "Loading..."
                          : loanOfficers.length === 0
                            ? "No loan officers found"
                            : "Select loan officer"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {loanOfficers.length === 0 ? (
                      <SelectItem value="none" disabled>No loan officers available</SelectItem>
                    ) : (
                      loanOfficers.map((officer) => (
                        <SelectItem key={officer.id} value={officer.id}>
                          {officer.firstName} {officer.lastName}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="broker-split" className="text-blue-600 font-semibold">
                  Broker Split:
                </Label>
                <div className="relative">
                  <Input
                    id="broker-split"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={brokerSplit}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow whole numbers 0-100
                      if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 100)) {
                        brokerSplitResolutionRequestRef.current += 1;
                        setBrokerSplit(value === '' ? 0 : parseInt(value));
                        setBrokerSplitResolving(false);
                        setBrokerSplitResolutionError(null);
                        setBrokerSplitBasis({
                          source: 'manual',
                          historyId: null,
                          effectiveDate: null
                        });
                      }
                    }}
                    className="pr-8"
                    disabled={isReadOnly}
                    aria-describedby="broker-split-help"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
                <div id="broker-split-help" className="text-xs" aria-live="polite">
                  {brokerSplitResolving ? (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading the split effective on the funded date…
                    </span>
                  ) : brokerSplitResolutionError ? (
                    <span className="text-red-600">{brokerSplitResolutionError}</span>
                  ) : brokerSplitBasis?.source === 'history' ? (
                    <span className="text-muted-foreground">
                      Funding-date split from the schedule effective {formatDateForDisplay(brokerSplitBasis.effectiveDate)}.
                    </span>
                  ) : brokerSplitBasis?.source === 'snapshot' ? (
                    <span className="text-muted-foreground">
                      Saved payout snapshot. Later compensation changes do not overwrite it.
                    </span>
                  ) : brokerSplitBasis?.source === 'manual' ? (
                    <span className="text-amber-700">Manual payout override.</span>
                  ) : (
                    <span className="text-muted-foreground">
                      Select a loan officer and funded date to load the effective split.
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Mentor Info Banner */}
            {mentorData && (
              <Alert className="border-purple-300 bg-purple-50 dark:bg-purple-950">
                <AlertDescription className="text-purple-700 dark:text-purple-300 text-sm">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200">
                        Mentorship
                      </Badge>
                      <span>
                        Mentor: <strong>{mentorData.mentorName}</strong> ({mentorData.mentorSplitPercent}% of LO commission)
                      </span>
                    </div>
                    <Badge variant={mentorData.brokeredDealsCompleted >= 5 ? 'default' : 'secondary'}>
                      {mentorData.brokeredDealsCompleted >= 5
                        ? 'Graduated'
                        : `${mentorData.brokeredDealsCompleted}/5 deals`
                      }
                    </Badge>
                  </div>
                </AlertDescription>
              </Alert>
            )}

          </CardContent>
        </Card>
      </div>

      {/* Gross Check Reconciliation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-center">GROSS CHECK RECONCILIATION</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Row 1: Gross Check and Total Fees side by side */}
          <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="space-y-1">
              <Label htmlFor="gross-check" className="text-blue-600 font-semibold text-sm">
                Gross Check Received:
              </Label>
              <Input
                id="gross-check"
                type="text"
                value={grossCheckInput}
                onChange={(e) => setGrossCheckInput(e.target.value)}
                disabled={isReadOnly}
                onBlur={() => {
                  const num = parseFloat(grossCheckInput.replace(/[^0-9.]/g, ''));
                  if (!isNaN(num) && num > 0) {
                    setGrossCheckReceived(num);
                    setGrossCheckInput(formatCurrency(num));
                  } else {
                    setGrossCheckReceived(0);
                    setGrossCheckInput('');
                  }
                }}
                onFocus={() => {
                  // Show raw number when focused for easier editing
                  if (grossCheckReceived > 0) {
                    setGrossCheckInput(grossCheckReceived.toString());
                  }
                }}
                placeholder="$0.00"
                className="text-right font-medium"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Total Fees (below):</Label>
              <div className="h-10 flex items-center justify-end px-3 border rounded-md bg-muted/50">
                <span className="font-medium">{formatCurrency(totalFees)}</span>
              </div>
            </div>
          </div>

          {/* Row 2: Variance centered */}
          <div className="flex justify-center items-center gap-3 pt-2 border-t max-w-2xl mx-auto">
            <span className="font-semibold">Variance:</span>
            <span className={`text-xl font-bold ${isReconciled ? 'text-green-600' : 'text-red-600'}`}>
              {checkVariance >= 0 ? '+' : ''}{formatCurrency(checkVariance)}
            </span>
            {isReconciled ? (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Reconciled
              </Badge>
            ) : (
              <Badge variant="destructive">
                Unbalanced
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lender Fees Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-center">LENDER FEES</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Fee Name</TableHead>
                  <TableHead className="text-right">Total Fee</TableHead>
                  <TableHead className="text-right">Borrower PAC</TableHead>
                  <TableHead className="text-right">Borrower POC</TableHead>
                  <TableHead className="text-right">Lender POC</TableHead>
                  <TableHead className="text-right">Reimbursement</TableHead>
                  <TableHead>Reimburse To</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lenderFees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="h-12 w-12 text-muted-foreground/50" />
                        <p className="font-medium">No fees added yet</p>
                        <p className="text-sm">Upload a Settlement Statement to auto-extract fees, or add them manually below</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {lenderFees.map((fee) => (
                      <TableRow key={fee.id}>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteFee(fee.id)}
                            disabled={isReadOnly}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">{fee.name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(fee.totalFee)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(fee.borrowerPAC)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(fee.borrowerPOC)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(fee.lenderPOC)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(fee.reimbursement)}</TableCell>
                        <TableCell>
                          <Select value={fee.reimburseTo} disabled={isReadOnly} onValueChange={(value) => {
                            setLenderFees(lenderFees.map(f => {
                              if (f.id === fee.id) {
                                // Auto-populate reimbursement when LO or Mentor selected - use totalFee (not lenderPOC)
                                const newReimbursement = (value === 'Loan Officer' || value === 'Mentor') ? f.totalFee : 0;
                                return { ...f, reimburseTo: value as LenderFee['reimburseTo'], reimbursement: newReimbursement };
                              }
                              return f;
                            }));
                          }}>
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NA">NA</SelectItem>
                              <SelectItem value="Loan Officer">Loan Officer</SelectItem>
                              {mentorData && <SelectItem value="Mentor">Mentor</SelectItem>}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-bold bg-muted/50">
                      <TableCell></TableCell>
                      <TableCell>TOTAL FEES:</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalFees)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalBorrowerPAC)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalBorrowerPOC)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalLenderPOC)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalReimbursement)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Add Fee Section — hidden in read-only mode */}
          {!isReadOnly && <div className="mt-6 pt-6 border-t">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="w-[40px]"></TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Label htmlFor="add-fee" className="text-sm">Fee Name:</Label>
                      <Input
                        id="add-fee"
                        value={newFeeName}
                        onChange={(e) => setNewFeeName(e.target.value)}
                        placeholder="Fee name"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
handleAddFee();
}
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Label className="text-sm">Total Fee:</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="$0.00"
                        value={newFeeData.totalFee}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty, numbers, and decimals only (max 2 decimal places)
                          if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                            // Also auto-update reimbursement if "Loan Officer" or "Mentor" is selected
                            const newReimbursement = (newFeeData.reimburseTo === 'Loan Officer' || newFeeData.reimburseTo === 'Mentor') ? value : newFeeData.reimbursement;
                            setNewFeeData(prev => ({ ...prev, totalFee: value, reimbursement: newReimbursement }));
                          }
                        }}
                        className="text-right"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Label className="text-sm">Borrower PAC:</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="$0.00"
                        value={newFeeData.borrowerPAC}
                        onChange={(e) => handleNewFeeChange('borrowerPAC', e.target.value)}
                        className="text-right"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Label className="text-sm">Borrower POC:</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="$0.00"
                        value={newFeeData.borrowerPOC}
                        onChange={(e) => handleNewFeeChange('borrowerPOC', e.target.value)}
                        className="text-right"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Label className="text-sm">Lender POC:</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="$0.00"
                        value={newFeeData.lenderPOC}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty, numbers, and decimals only (max 2 decimal places)
                          if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                            setNewFeeData(prev => ({ ...prev, lenderPOC: value }));
                          }
                        }}
                        className="text-right"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Label className="text-sm">Reimbursement:</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="$0.00"
                        value={newFeeData.reimbursement}
                        onChange={(e) => handleNewFeeChange('reimbursement', e.target.value)}
                        className="text-right"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Label className="text-sm">Reimburse To:</Label>
                      <Select
                        value={newFeeData.reimburseTo}
                        onValueChange={(value) => {
                          // Auto-populate reimbursement when LO or Mentor selected - use totalFee (not lenderPOC)
                          const totalFee = parseFloat(newFeeData.totalFee) || 0;
                          const newReimbursement = (value === 'Loan Officer' || value === 'Mentor') ? totalFee.toString() : '';
                          setNewFeeData(prev => ({
                            ...prev,
                            reimburseTo: value as 'NA' | 'Loan Officer' | 'Mentor',
                            reimbursement: newReimbursement
                          }));
                        }}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NA">NA</SelectItem>
                          <SelectItem value="Loan Officer">Loan Officer</SelectItem>
                          {mentorData && <SelectItem value="Mentor">Mentor</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button onClick={handleAddFee} className="w-full mt-7">Add</Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>}
        </CardContent>
      </Card>

      {/* Commission Breakdown Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-center">COMMISSION BREAKDOWN</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Pre-Split Deductions */}
            <div className="max-w-md mx-auto space-y-2">
              <div className="flex justify-between items-center py-1">
                <span className="text-muted-foreground">Base Check (Expected):</span>
                <span className="font-medium">{formatCurrency(baseCheck)}</span>
              </div>
              <div className="flex justify-between items-center py-1 text-red-600">
                <span>Less: Processing Fee (Lendwise):</span>
                <span>-{formatCurrency(PROCESSING_FEE)}</span>
              </div>
              <div className="flex justify-between items-center py-1 text-red-600">
                <span>Less: Reimbursements:</span>
                <span>-{formatCurrency(totalAllReimbursements)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-t font-bold">
                <span>Net for Commission Split:</span>
                <span className="text-lg">{formatCurrency(netForSplit)}</span>
              </div>
            </div>

            {/* LO/Mentor/Company breakdown */}
            <div className={`grid grid-cols-1 ${mentorData ? 'lg:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
              {/* Loan Officer Column */}
              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950">
                <h3 className="font-bold text-center text-blue-700 dark:text-blue-300 mb-4">
                  LOAN OFFICER ({brokerSplit}% Split)
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1">
                    <span className="text-sm">Commission ({brokerSplit}% × {formatCurrency(netForSplit)}):</span>
                    <span className="font-medium">{formatCurrency(loCommissionBase)}</span>
                  </div>
                  {mentorData && (
                    <div className="flex justify-between items-center py-1 text-purple-600">
                      <span className="text-sm">Less: Mentor Split ({mentorSplitPercent}%):</span>
                      <span className="font-medium">-{formatCurrency(mentorCommission)}</span>
                    </div>
                  )}
                  {totalLOReimbursements > 0 && (
                    <div className="flex justify-between items-center py-1 text-green-600">
                      <span className="text-sm">Plus: Reimbursements:</span>
                      <span className="font-medium">+{formatCurrency(totalLOReimbursements)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-t border-blue-200 dark:border-blue-800">
                    <span className="font-bold">LO GROSS:</span>
                    <span className="text-xl font-bold text-blue-700 dark:text-blue-300">
                      {formatCurrency(mentorData ? loTotalAfterMentor : loTotalCommission)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1 text-red-600">
                    <span className="text-sm">Less: E&O Insurance:</span>
                    <span className="font-medium">-{formatCurrency(loEAndO)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-t border-blue-200 dark:border-blue-800">
                    <span className="font-bold">LO NET PAYOUT:</span>
                    <span className="text-lg font-bold text-blue-700 dark:text-blue-300">
                      {formatCurrency((mentorData ? loTotalAfterMentor : loTotalCommission) - loEAndO)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Mentor Column (conditional) */}
              {mentorData && (
                <div className="border rounded-lg p-4 bg-purple-50 dark:bg-purple-950">
                  <h3 className="font-bold text-center text-purple-700 dark:text-purple-300 mb-4">
                    MENTOR: {mentorData.mentorName.toUpperCase()}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-sm">{mentorSplitPercent}% of LO Commission:</span>
                      <span className="font-medium">{formatCurrency(mentorCommission)}</span>
                    </div>
                    {totalMentorReimbursements > 0 && (
                      <div className="flex justify-between items-center py-1 text-green-600">
                        <span className="text-sm">Plus: Reimbursements:</span>
                        <span className="font-medium">+{formatCurrency(totalMentorReimbursements)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2 border-t border-purple-200 dark:border-purple-800">
                      <span className="font-bold">MENTOR GROSS:</span>
                      <span className="text-xl font-bold text-purple-700 dark:text-purple-300">
                        {formatCurrency(mentorTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1 text-red-600">
                      <span className="text-sm">Less: E&O Insurance:</span>
                      <span className="font-medium">-{formatCurrency(mentorEAndO)}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-t border-purple-200 dark:border-purple-800">
                      <span className="font-bold">MENTOR NET PAYOUT:</span>
                      <span className="text-lg font-bold text-purple-700 dark:text-purple-300">
                        {formatCurrency(mentorTotal - mentorEAndO)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Company Column */}
              <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950">
                <h3 className="font-bold text-center text-green-700 dark:text-green-300 mb-4">
                  COMPANY REVENUE ({100 - brokerSplit}% Split)
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1">
                    <span className="text-sm">Commission ({100 - brokerSplit}% × {formatCurrency(netForSplit)}):</span>
                    <span className="font-medium">{formatCurrency(companyCommissionBase)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 text-green-600">
                    <span className="text-sm">Plus: Processing Fee:</span>
                    <span className="font-medium">+{formatCurrency(PROCESSING_FEE)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 text-green-600">
                    <span className="text-sm">Plus: E&O Insurance:</span>
                    <span className="font-medium">+{formatCurrency(EO_INSURANCE)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-t border-green-200 dark:border-green-800">
                    <span className="font-bold">COMPANY TOTAL:</span>
                    <span className="text-xl font-bold text-green-700 dark:text-green-300">
                      {formatCurrency(companyTotalRevenue)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Upload Section - NEW: Single Smart Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-center">LOAN DOCUMENTS</CardTitle>
          <p className="text-sm text-muted-foreground text-center mt-2">
            Upload loan documents - we'll figure out what they are automatically
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Upload Zone */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors bg-gray-50/50">
            <input
              type="file"
              id="document-upload"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleDocumentUpload}
              disabled={uploading || isReadOnly}
            />
            <label
              htmlFor="document-upload"
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              <div className="rounded-full bg-blue-100 p-4">
                <Upload className="h-8 w-8 text-blue-600" />
              </div>
              <div className="space-y-1">
                <div className="text-sm text-gray-600">
                  {uploading ? (
                    <span className="text-blue-600 font-medium">Analyzing document...</span>
                  ) : (
                    <>
                      <span className="font-medium text-blue-600">Click to upload</span>
                      <span className="text-gray-500"> or drag and drop</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  Settlement Statement, Final Check, or other loan documents
                </div>
                <div className="text-xs text-gray-400">
                  PDF, PNG, JPG (MAX. 10MB)
                </div>
              </div>
            </label>
          </div>

          {/* Uploaded Documents - Compact Grid Layout */}
          {uploadedDocuments.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700">Uploaded Documents:</Label>

              {/* Processing Progress Bar */}
              {uploadedDocuments.some(doc => doc.processingStatus && doc.processingStatus !== 'complete' && doc.processingStatus !== 'failed') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">
                      {uploadedDocuments.some(doc => doc.processingStatus === 'classifying')
                        ? 'Classifying document with AI...'
                        : 'Extracting text from document...'}
                    </span>
                  </div>
                  <Progress
                    value={Math.round(
                      uploadedDocuments
                        .filter(doc => doc.processingStatus && doc.processingStatus !== 'complete' && doc.processingStatus !== 'failed')
                        .reduce((sum, doc) => sum + (doc.progress || 0), 0) /
                      Math.max(1, uploadedDocuments.filter(doc => doc.processingStatus && doc.processingStatus !== 'complete' && doc.processingStatus !== 'failed').length)
                    )}
                    className="h-2"
                  />
                </div>
              )}

              {uploadedDocuments.map((doc) => {
                const typeInfo = getDocumentTypeInfo(doc.documentType);
                const splitResult = doc.extractedFields?.splitResult;

                return (
                  <div
                    key={doc.id}
                    className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                  >
                    {/* Combined document: 3-column grid */}
                    {doc.documentType === 'combined' ? (
                      <div className="grid grid-cols-3 gap-4">
                        {/* Column 1: Original PDF */}
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-gray-500 uppercase">Original</div>
                          {renamingSplitFile === `${doc.id}:original` ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={splitRenameValue}
                                onChange={(e) => setSplitRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
handleSaveRenameSplit(doc.id, 'original');
}
                                  if (e.key === 'Escape') {
handleCancelRenameSplit();
}
                                }}
                                className="h-6 text-xs w-32"
                                autoFocus
                              />
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-green-600" onClick={() => handleSaveRenameSplit(doc.id, 'original')}>
                                <Check className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-500" onClick={handleCancelRenameSplit}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => doc.storagePath && handleViewDocument(doc.storagePath, doc.splitFileNames?.original || doc.fileName || 'Combined.pdf')}
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm"
                              >
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate max-w-[120px]">{doc.splitFileNames?.original || doc.fileName || 'Combined.pdf'}</span>
                              </button>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-400 hover:text-gray-600" onClick={() => handleStartRenameSplit(doc.id, 'original', doc.splitFileNames?.original || doc.fileName || 'Combined.pdf')} disabled={isReadOnly}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400 hover:text-red-600" onClick={() => doc.storagePath && handleDeleteSplitFile(doc.id, doc.storagePath, 'original')} disabled={isReadOnly}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Column 2: Split Documents */}
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-gray-500 uppercase">Split Files</div>
                          {/* A reported split failure takes precedence: a partial
                              result cannot be pushed, so surface the reason and
                              the retry rather than a half-populated file list. */}
                          {splitResult && !doc.splitError ? (
                            <div className="space-y-1">
                              {splitResult.settlementStatementPath && (
                                renamingSplitFile === `${doc.id}:settlement` ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={splitRenameValue}
                                      onChange={(e) => setSplitRenameValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
handleSaveRenameSplit(doc.id, 'settlement');
}
                                        if (e.key === 'Escape') {
handleCancelRenameSplit();
}
                                      }}
                                      className="h-6 text-xs w-40"
                                      autoFocus
                                    />
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-green-600" onClick={() => handleSaveRenameSplit(doc.id, 'settlement')}>
                                      <Check className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-500" onClick={handleCancelRenameSplit}>
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleViewDocument(splitResult.settlementStatementPath)}
                                      className="flex items-center gap-1 text-green-600 hover:text-green-800 hover:underline text-sm"
                                    >
                                      <FileText className="h-4 w-4 flex-shrink-0" />
                                      <span className="truncate max-w-[160px]">{doc.splitFileNames?.settlementStatement || splitResult.settlementStatementPath.split('/').pop()}</span>
                                    </button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-400 hover:text-gray-600" onClick={() => handleStartRenameSplit(doc.id, 'settlement', doc.splitFileNames?.settlementStatement || splitResult.settlementStatementPath.split('/').pop() || '')} title="Rename" disabled={isReadOnly}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-400 hover:text-blue-600" onClick={() => handleRotateDocument(splitResult.settlementStatementPath)} title="Rotate 90° clockwise" disabled={isReadOnly}>
                                      <RotateCw className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400 hover:text-red-600" onClick={() => handleDeleteSplitFile(doc.id, splitResult.settlementStatementPath, 'settlement')} title="Delete" disabled={isReadOnly}>
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )
                              )}
                              {splitResult.brokerCheckPath && (
                                renamingSplitFile === `${doc.id}:check` ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={splitRenameValue}
                                      onChange={(e) => setSplitRenameValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
handleSaveRenameSplit(doc.id, 'check');
}
                                        if (e.key === 'Escape') {
handleCancelRenameSplit();
}
                                      }}
                                      className="h-6 text-xs w-40"
                                      autoFocus
                                    />
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-green-600" onClick={() => handleSaveRenameSplit(doc.id, 'check')}>
                                      <Check className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-500" onClick={handleCancelRenameSplit}>
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleViewDocument(splitResult.brokerCheckPath)}
                                      className="flex items-center gap-1 text-green-600 hover:text-green-800 hover:underline text-sm"
                                    >
                                      <FileText className="h-4 w-4 flex-shrink-0" />
                                      <span className="truncate max-w-[160px]">{doc.splitFileNames?.brokerCheck || splitResult.brokerCheckPath.split('/').pop()}</span>
                                    </button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-400 hover:text-gray-600" onClick={() => handleStartRenameSplit(doc.id, 'check', doc.splitFileNames?.brokerCheck || splitResult.brokerCheckPath.split('/').pop() || '')} title="Rename" disabled={isReadOnly}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-gray-400 hover:text-blue-600" onClick={() => handleRotateDocument(splitResult.brokerCheckPath)} title="Rotate 90° clockwise" disabled={isReadOnly}>
                                      <RotateCw className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400 hover:text-red-600" onClick={() => handleDeleteSplitFile(doc.id, splitResult.brokerCheckPath, 'check')} title="Delete" disabled={isReadOnly}>
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )
                              )}
                            </div>
                          ) : doc.splitError ? (
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-destructive">
                                Split unavailable
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {doc.splitError}
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRetrySplit(doc.id)}
                                disabled={doc.retryingSplit || isReadOnly}
                              >
                                {doc.retryingSplit ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                  <RotateCw className="h-4 w-4 mr-1" />
                                )}
                                {doc.retryingSplit ? 'Retrying...' : 'Retry split'}
                              </Button>
                            </div>
                          ) : (
                            <div className="text-amber-600 text-sm">⏳ Split pending...</div>
                          )}
                        </div>

                        {/* Column 3: Actions */}
                        <div className="flex items-center justify-end gap-2">
                          {/* Both split documents must exist: pushing a combined
                              document sends the settlement statement and the
                              broker check as two separate uploads. */}
                          {!doc.pushedToEncompass
                            && splitResult?.settlementStatementPath
                            && splitResult?.brokerCheckPath && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handlePushToEncompass(doc.id)}
                              disabled={doc.pushingToEncompass || !selectedLoan || !isReconciled || isReadOnly}
                              title={!selectedLoan ? 'Select a loan first' : !isReconciled ? 'Reconciliation must balance first' : 'Push split docs to Encompass'}
                            >
                              {doc.pushingToEncompass ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <CloudUpload className="h-4 w-4 mr-1" />
                              )}
                              {doc.pushingToEncompass ? 'Pushing...' : 'Push to Encompass'}
                            </Button>
                          )}
                          {doc.pushedToEncompass && (
                            <Badge variant="secondary" className="text-green-600 bg-green-50">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Pushed
                            </Badge>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Individual document: Simple row */
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-xl">{typeInfo.icon}</div>
                          <div>
                            {renamingDocId === doc.id ? (
                              /* Rename mode */
                              <div className="flex items-center gap-2">
                                <Input
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
handleSaveRename(doc.id);
}
                                    if (e.key === 'Escape') {
handleCancelRename();
}
                                  }}
                                  className="h-7 text-sm w-48"
                                  autoFocus
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-green-600 hover:text-green-700"
                                  onClick={() => handleSaveRename(doc.id)}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-gray-500 hover:text-gray-700"
                                  onClick={handleCancelRename}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              /* Normal display mode */
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => doc.storagePath && handleViewDocument(doc.storagePath, doc.fileName || doc.file?.name || 'Document')}
                                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-sm"
                                >
                                  {doc.fileName || doc.file?.name || 'Document'}
                                </button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 text-gray-400 hover:text-gray-600"
                                  onClick={() => handleStartRename(doc)}
                                  title="Rename document"
                                  disabled={isReadOnly}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="secondary" className="text-xs">
                                {typeInfo.label}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.documentType !== 'unknown' && !doc.pushedToEncompass && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handlePushToEncompass(doc.id)}
                              disabled={doc.pushingToEncompass || !selectedLoan || !isReconciled || isReadOnly}
                              title={!selectedLoan ? 'Select a loan first' : !isReconciled ? 'Reconciliation must balance first' : 'Push to Encompass'}
                            >
                              {doc.pushingToEncompass ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <CloudUpload className="h-4 w-4 mr-1" />
                              )}
                              {doc.pushingToEncompass ? 'Pushing...' : 'Push to Encompass'}
                            </Button>
                          )}
                          {doc.pushedToEncompass && (
                            <Badge variant="secondary" className="text-green-600 bg-green-50">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Pushed
                            </Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            onClick={() => handleRemoveDocument(doc.id)}
                            disabled={isReadOnly}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isPaid ? (
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-base py-1 px-3">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Paid
            </Badge>
          ) : isFinalized ? (
            <>
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-base py-1 px-3">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Pushed to Accounting
              </Badge>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
                    Reopen
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reopen Reconciliation?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will revert the payout status to draft, making it editable again.
                      {mentorData && ' The companion mentor payout will also be reverted.'}
                      {' '}The loan officer compensation entry will return to pending status.
                      Note: brokered deal counts are not adjusted.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleReopenPayout}>Reopen</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <>
              <Checkbox
                id="resend-pdf"
                checked={resendPDF}
                onCheckedChange={(checked) => setResendPDF(checked as boolean)}
                disabled={isReadOnly}
              />
              <Label htmlFor="resend-pdf">
                Resend PDF to Accounting After Save:
              </Label>
            </>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            size="lg"
            disabled={isReadOnly || brokerSplitResolving}
            onClick={handleSaveDraft}
          >
            Save Draft
          </Button>
          <Button
            size="lg"
            onClick={handlePushToAccounting}
            disabled={!canPushToAccounting || pushingToAccounting}
            title={pushToAccountingTitle}
          >
            {pushingToAccounting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Pushing...
              </>
            ) : isReadOnly ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Finalized
              </>
            ) : (
              'Push to Accounting'
            )}
          </Button>
        </div>
      </div>
      </>)}
    </div>
  );
};
